import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { Button, Container, Grid, Typography } from "@material-ui/core";
import { Helmet } from "react-helmet-async";

// Canvas preview-safe fallback.
// Enclopedia本番環境では以下に置き換えてください。
// import { headRewrite } from "../function/headRewrite";
const headRewrite = () => {};

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#eeeeee",
    color: "#111",
    minHeight: "100vh",
    margin: 0,
    padding: 0,
    fontFamily:
      '"Helvetica Neue", Arial, "Noto Sans JP", "Yu Gothic", sans-serif',
  },
  pageFrame: {
    background: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      borderLeft: "none",
      borderRight: "none",
    },
  },
  hero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(6, 0, 7),
    },
  },
  section: {
    padding: theme.spacing(10, 0),
    borderBottom: "1px solid #c8c8c8",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 0),
    },
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    fontSize: 12,
    letterSpacing: "0.12em",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  labelLine: {
    flex: 1,
    height: 1,
    background: "#111",
    opacity: 0.7,
  },
  heroTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.18,
    letterSpacing: "-0.08em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  heading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(28px, 4vw, 48px)",
    lineHeight: 1.45,
    letterSpacing: "-0.05em",
    fontWeight: 500,
    marginBottom: theme.spacing(4),
  },
  subHeading: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", "Hiragino Mincho ProN", serif',
    fontSize: "clamp(22px, 2.8vw, 34px)",
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    fontWeight: 500,
    marginBottom: theme.spacing(2.5),
  },
  heroBody: {
    maxWidth: 700,
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    marginBottom: theme.spacing(4),
    whiteSpace: "pre-line",
  },
  body: {
    fontSize: 15,
    lineHeight: 2.1,
    color: "#222",
    whiteSpace: "pre-line",
  },
  noteBody: {
    fontSize: 14,
    lineHeight: 2,
    color: "#333",
    whiteSpace: "pre-line",
  },
  noticeBody: {
    fontSize: 13,
    lineHeight: 1.9,
    color: "#333",
    whiteSpace: "pre-line",
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
  primaryButton: {
    background: "#111",
    color: "#eeeeee",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#eeeeee",
      color: "#111",
      boxShadow: "none",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#111",
    border: "1px solid #111",
    borderRadius: 0,
    padding: theme.spacing(1.6, 3.5),
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
      boxShadow: "none",
    },
  },
  textLink: {
    color: "#111",
    textDecoration: "none",
    borderBottom: "1px solid #111",
    paddingBottom: 4,
    fontSize: 14,
    fontWeight: 700,
  },
  keywordLine: {
    borderTop: "1px solid #111",
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    fontSize: 13,
    color: "#333",
  },
  heroVisual: {
    position: "relative",
    minHeight: 540,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      minHeight: 390,
      marginTop: theme.spacing(6),
    },
  },
  heroCircle: {
    position: "absolute",
    right: -40,
    bottom: 22,
    width: 270,
    height: 270,
    border: "1px solid #111",
    borderRadius: "50%",
    opacity: 0.65,
    [theme.breakpoints.down("sm")]: {
      width: 180,
      height: 180,
      right: -60,
    },
  },
  heroSquare: {
    position: "absolute",
    left: 24,
    top: 44,
    width: 190,
    height: 190,
    border: "1px solid #111",
    opacity: 0.55,
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
      left: 0,
    },
  },
  heroNumber: {
    position: "absolute",
    top: 20,
    right: 0,
    writingMode: "vertical-rl",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
  },
  heroMock: {
    position: "relative",
    width: "76%",
    maxWidth: 430,
    aspectRatio: "1 / 1",
    border: "1px solid #111",
    background: "rgba(255, 255, 255, 0.18)",
    overflow: "hidden",
    transform: "rotate(2deg)",
    [theme.breakpoints.down("sm")]: {
      width: "84%",
      maxWidth: 320,
    },
  },
  heroMockImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  heroMockLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  heroMockTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(24px, 3vw, 36px)",
    lineHeight: 1.5,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  thesisCard: {
    border: "1px solid #111",
    minHeight: 315,
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
    [theme.breakpoints.down("sm")]: {
      minHeight: "auto",
    },
  },
  thesisLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  thesisText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(21px, 2.4vw, 30px)",
    lineHeight: 1.72,
    letterSpacing: "-0.03em",
    fontWeight: 500,
  },
  cardNumber: {
    fontSize: 24,
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.02em",
    marginBottom: theme.spacing(2),
  },
  smallRule: {
    width: 32,
    height: 1,
    background: "#111",
    marginBottom: theme.spacing(3),
  },
  valuationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  valuationCard: {
    border: "1px solid #111",
    minHeight: 315,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: "transparent",
  },
  brandGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  brandItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 192,
  },
  brandTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(1.4),
  },
  brandTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 20,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  listBlock: {
    borderTop: "1px solid #111",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "72px 280px 1fr",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
    borderBottom: "1px solid #c8c8c8",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1.5),
    },
  },
  listIndex: {
    fontSize: 14,
    fontWeight: 700,
    color: "#555",
  },
  listTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
  },
  quoteBlock: {
    border: "1px solid #111",
    padding: theme.spacing(5),
    position: "relative",
    background: "#111",
    color: "#eeeeee",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 3),
    },
  },
  quoteMark: {
    fontFamily: '"Times New Roman", serif',
    fontSize: 72,
    lineHeight: 0.8,
    opacity: 0.75,
    marginBottom: theme.spacing(2),
  },
  quoteText: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(22px, 3vw, 36px)",
    lineHeight: 1.72,
    letterSpacing: "-0.04em",
    fontWeight: 500,
  },
  flowGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: theme.spacing(2.5),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  flowCard: {
    border: "1px solid #111",
    minHeight: 255,
    padding: theme.spacing(2.8),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  flowStep: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  flowTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 22,
    lineHeight: 1.5,
    letterSpacing: "-0.03em",
    marginBottom: theme.spacing(1.5),
  },
  assuranceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    borderTop: "1px solid #c8c8c8",
    borderLeft: "1px solid #c8c8c8",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  assuranceItem: {
    borderRight: "1px solid #c8c8c8",
    borderBottom: "1px solid #c8c8c8",
    padding: theme.spacing(2.8, 2.2),
    minHeight: 180,
  },
  assuranceTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.6,
    marginBottom: theme.spacing(1.5),
  },
  noteBox: {
    marginTop: theme.spacing(4),
    border: "1px solid #111",
    padding: theme.spacing(3),
    background: "rgba(255,255,255,0.18)",
  },
  faqGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  faqItem: {
    border: "1px solid #111",
    padding: theme.spacing(3),
  },
  faqQuestion: {
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.8,
    marginBottom: theme.spacing(1.5),
  },
  finalCta: {
    margin: theme.spacing(8, 0),
    border: "1px solid #111",
    padding: theme.spacing(7, 3),
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  finalTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: "clamp(30px, 4.8vw, 58px)",
    lineHeight: 1.35,
    letterSpacing: "-0.05em",
    marginBottom: theme.spacing(2),
  },
  finalButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2.5),
    flexWrap: "wrap",
  },
  stripe: {
    position: "absolute",
    right: 20,
    top: 30,
    bottom: 30,
    width: 48,
    background:
      "repeating-linear-gradient(90deg, transparent 0, transparent 7px, #111 8px, #111 9px)",
    opacity: 0.6,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  indexHero: {
    padding: theme.spacing(8, 0, 10),
    borderBottom: "1px solid #c8c8c8",
  },
  indexIntro: {
    maxWidth: 820,
  },
  articleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  articleCard: {
    border: "1px solid #111",
    minHeight: 330,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#111",
    textDecoration: "none",
    transition: "background 0.2s ease, color 0.2s ease",
    "&:hover": {
      background: "#111",
      color: "#eeeeee",
    },
  },
  articleCategory: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: theme.spacing(2),
  },
  articleTitle: {
    fontFamily: '"Noto Serif JP", "Yu Mincho", serif',
    fontSize: 24,
    lineHeight: 1.55,
    letterSpacing: "-0.04em",
    marginBottom: theme.spacing(2),
  },
  articleCardBody: {
    fontSize: 14,
    lineHeight: 2,
  },
  articleArrow: {
    marginTop: theme.spacing(3),
    fontWeight: 700,
    fontSize: 14,
  },
  notFound: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(8, 0),
  },
}));

const brandBuybackArticles = [
  {
    slug: "comme-des-garcons",
    category: "BRAND BUYBACK",
    listTitle: "コムデギャルソン買取",
    listDescription:
      "シーズン背景、コレクション性、希少性まで見て査定するEnclopediaのブランド別買取ページ。",
    seo: {
      title:
        "コムデギャルソン買取｜アーカイブ価値まで見て査定するEnclopedia",
      description:
        "COMME des GARÇONSの買取ならEnclopediaへ。シーズン背景、コレクション性、希少性まで見て査定します。高価買取を目指しつつ、まずはLINE事前査定から宅配買取へご案内します。",
      keyword:
        "コムデギャルソン 買取,COMME des GARCONS 買取,コムデギャルソン 高価買取,コムデギャルソン 宅配買取,COMME des GARÇONS HOMME PLUS 買取,JUNYA WATANABE 買取,noir kei ninomiya 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/comme-des-garcons",
    },
    hero: {
      label: "COMME DES GARÇONS BUYBACK",
      number: "01 / BUYBACK",
      titleLines: [
        "コムデギャルソン買取。",
        "アーカイブ価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、COMME des GARÇONSをブランド名だけで判断しません。\nシーズン背景、コレクション性、希少性まで確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで事前にご相談いただき、納得感のある宅配買取へつなげます。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref: "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "buyback",
        "archive valuation",
        "high-value purchase",
        "comme des garçons",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: ["ブランド名ではなく、", "服の背景まで見る。"],
      image: "media/archive_fashion/buyback1.png",
      imageAlt: "コムデギャルソン買取のメイン画像",
    },
    thesis: {
      text:
        "高価買取を目指しながら、シーズン背景とアーカイブ価値を査定に反映する。",
      keywords: ["season", "collection", "rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: ["コムデギャルソンの価値は、", "相場表だけでは測れない。"],
      body:
        "新しいシーズンの方が高値になりやすい場面はあります。\nただし、Enclopediaは年代だけで一律に判断しません。\nその服がどのシーズンに属し、どのようなコレクション性を持ち、いまどれほど希少かを見ながら、アイテム単位で査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: ["Enclopediaが重視する、", "3つの査定視点。"],
      items: [
        {
          number: "01",
          title: "シーズン背景を見る",
          text:
            "単にブランド名や型番だけで判断せず、どの時期の思想やテーマの中で生まれた服かを確認します。",
        },
        {
          number: "02",
          title: "コレクション性を見る",
          text:
            "ランウェイ登場品、シーズンの象徴性が強いデザイン、当時のテーマをよく示すピースは丁寧に評価します。",
        },
        {
          number: "03",
          title: "希少性を見る",
          text:
            "市場流通の少なさ、サイズ、保存状態、再入手難度まで含めて、個々のアイテム単位で査定します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET LINES",
      titleLines: ["COMME des GARÇONS全般を、", "幅広く査定します。"],
      items: [
        {
          tag: "LINE 01",
          title: "COMME des GARÇONS",
          text:
            "本ラインをはじめ、ブランドの核となるアーカイブや象徴的なコレクションピースを査定します。",
        },
        {
          tag: "LINE 02",
          title: "COMME des GARÇONS HOMME PLUS",
          text:
            "コレクション性が高く、Enclopediaが特に注力して見たいラインです。近年シーズンから過去作まで個別評価します。",
        },
        {
          tag: "LINE 03",
          title: "COMME des GARÇONS HOMME / COMME des GARÇONS HOMME DEUX",
          text:
            "日常着に近いラインでも、素材使い・年代・ディテールによって価値が変わるため丁寧に見ます。",
        },
        {
          tag: "LINE 04",
          title: "COMME des GARÇONS SHIRT / COMME des GARÇONS COMME des GARÇONS",
          text:
            "シャツ、ワンピース、女性向けラインも対象。ブランドの個性が強い品は積極的に査定します。",
        },
        {
          tag: "LINE 05",
          title: "JUNYA WATANABE",
          text:
            "構築性やコラボレーション性が高いアイテムを中心に、背景を踏まえて査定します。",
        },
        {
          tag: "LINE 06",
          title: "noir kei ninomiya",
          text:
            "カッティング、構造、加工が際立つ作品を評価。現行・過去シーズン問わずご相談いただけます。",
        },
        {
          tag: "LINE 07",
          title: "PLAY COMME des GARÇONS / Wallet等",
          text:
            "幅広いラインに対応します。ただし査定額は市場性・状態・需要により個別に判断します。",
        },
        {
          tag: "LINE 08",
          title: "その他関連ライン",
          text:
            "tricot、robe de chambre、GANRYU、BLACKなど、関連ブランドも内容に応じて査定します。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: ["高価買取につながりやすい、", "コムデギャルソン。"],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のような服を丁寧に見ます。",
      items: [
        {
          index: "01",
          title: "近年シーズンの人気アイテム",
          text:
            "現時点では比較的新しいシーズンの方が相場上高値になりやすい傾向があります。特に人気の強いアウター、ジャケット、ワンピースはご相談ください。",
        },
        {
          index: "02",
          title: "ランウェイ登場品・シーズンテーマを象徴する服",
          text:
            "コレクション全体の思想を担うルック、象徴性の強い柄や構造、ブランドの文脈が濃いアイテムは評価対象です。",
        },
        {
          index: "03",
          title: "希少アーカイブ・再入手しにくいピース",
          text:
            "古いだけで高くなるわけではありませんが、流通量が少なく、今なお評価される作品は個別に価値を見ます。",
        },
        {
          index: "04",
          title: "再構築・縮絨・変形・立体裁断などの代表的ディテール",
          text:
            "COMME des GARÇONSらしさが強く現れる技法や造形は、アイテムそのものの魅力として査定に反映します。",
        },
      ],
    },
    quote: {
      textLines: ["売れる服としてではなく、", "次へ受け継ぐ価値のある服として見る。"],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: ["一般的な査定で、", "見落とされやすい価値。"],
      intro:
        "Enclopediaは、ブランド古着としての流通性だけでなく、アーカイブとしての文脈も確認します。",
      items: [
        {
          index: "01",
          title: "年代だけで一律に見ない",
          text:
            "“古いから高い”“新しいから高い”ではなく、その服がどのような評価軸を持つかを確認します。",
        },
        {
          index: "02",
          title: "型番相場だけで終わらせない",
          text:
            "過去の取引価格だけでなく、今の需要、コレクション文脈、海外評価まで含めて個別に考えます。",
        },
        {
          index: "03",
          title: "売れるかだけでなく、残す価値を見る",
          text:
            "Enclopediaはアーカイブをレンタル・共有するサービスでもあります。次の着用者へ渡す価値がある服を丁寧に受け取ります。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: ["LINE事前査定から、", "宅配買取へ。"],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。宅配買取へ進む前に、おおまかな査定可否をご案内します。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: ["相談しやすく、", "進めやすい宅配買取。"],
      intro:
        "LINEで相談してから進められるため、買取成立までの不安と手間を減らせます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、無駄な発送や手間を減らせます。",
        },
        {
          title: "送料は無料",
          text: "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: ["コムデギャルソン買取で、", "よくあるご質問。"],
      items: [
        {
          question: "どのラインのコムデギャルソンを買取していますか？",
          answer:
            "COMME des GARÇONS全般に対応しています。本ライン、COMME des GARCONS HOMME PLUS、COMME des GARCONS HOMME、COMME des GARCONS HOMME DEUX、SHIRT、COMME COMME、JUNYA WATANABE、noir kei ninomiyaなど幅広くご相談いただけます。",
        },
        {
          question: "古いアーカイブほど高く買い取ってもらえますか？",
          answer:
            "年代だけで一律には判断しません。現在は近年シーズンの方が高値になりやすい場合もありますが、過去作でもコレクション性・希少性・需要が高いものは個別に評価します。",
        },
        {
          question: "LINE査定をせずに宅配買取を申し込めますか？",
          answer:
            "申込自体は可能ですが、事前にLINE査定をご利用いただくことで、査定可否や大まかな方向性を確認しやすくなります。実務上もLINE事前査定をおすすめしています。",
        },
        {
          question: "キャンセルした場合、返送料はかかりますか？",
          answer:
            "基本的には無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
        },
        {
          question: "宅配キットは必ず使う必要がありますか？",
          answer:
            "必須ではありません。宅配キットは選択制のため、ご自身で梱包いただける場合はそのまま発送いただけます。",
        },
        {
          question: "査定では何を見ていますか？",
          answer:
            "状態や市場相場に加え、シーズン背景、コレクション性、希少性、現在の需要を総合して判断します。",
        },
      ],
    },
    finalCta: {
      titleLines: ["まずはLINEで、", "コムデギャルソンの価値をご相談ください。"],
      body:
        "宅配買取へ進む前に、写真で事前確認。\n背景まで見て査定するEnclopediaが、次の流通へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref: "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "sacai",
    category: "BRAND BUYBACK",
    listTitle: "sacai買取",
    listDescription:
      "異素材切替、ドッキング、コラボレーションの価値まで見て査定するEnclopediaのsacai専用買取ページ。",
    seo: {
      title:
        "sacai買取｜異素材切替・コラボ・アーカイブ価値まで見て査定するEnclopedia",
      description:
        "sacaiの買取ならEnclopediaへ。異素材切替、ドッキング構造、コラボレーション、シーズン性、希少性まで見て査定します。まずはLINE事前査定から宅配買取へご案内します。",
      keyword:
        "sacai 買取,サカイ 買取,sacai 高価買取,sacai 宅配買取,sacai アーカイブ 買取,sacai コラボ 買取,sacai Nike 買取,sacai WTAPS 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/sacai",
    },
    hero: {
      label: "SACAI BUYBACK",
      number: "02 / BUYBACK",
      titleLines: [
        "sacai買取。",
        "構造の価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、sacaiをブランド名だけで一律に判断しません。\n異素材切替、ドッキング構造、シーズン背景、コラボレーション性まで確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで事前にご相談いただき、納得感のある宅配買取へつなげます。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "buyback",
        "hybrid construction",
        "collaboration value",
        "sacai",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: ["素材の切替だけでなく、", "構造の背景まで見る。"],
      image: "media/archive_fashion/buyback2.png",
      imageAlt: "sacai買取のメイン画像",
    },
    thesis: {
      text:
        "sacaiの価値は、人気だけでなく、服の構造・シーズン性・協業背景まで見て判断する。",
      keywords: ["construction", "season", "rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "sacaiの価値は、",
        "“異素材ミックス”の一言では終わらない。",
      ],
      body:
        "sacaiは、見慣れた服の構造を組み替えることで新しい輪郭を生み出してきたブランドです。\nニットと布帛、テーラードとミリタリー、スカートとプリーツなど、異なる要素を一着の中で成立させる設計に特徴があります。\nEnclopediaでは、単なる人気ブランドとしてではなく、その服が持つ構造的な価値と市場性をあわせて見ます。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: ["Enclopediaが重視する、", "3つの査定視点。"],
      items: [
        {
          number: "01",
          title: "構造とデザイン性を見る",
          text:
            "異素材切替、ドッキング、レイヤード表現など、sacaiらしさがどの程度アイテムに表れているかを確認します。",
        },
        {
          number: "02",
          title: "シーズン性と希少性を見る",
          text:
            "どの時期の作品か、流通量がどの程度か、再入手しにくいピースかを踏まえて個別に判断します。",
        },
        {
          number: "03",
          title: "コラボレーション価値を見る",
          text:
            "WTAPS、NIKEをはじめとした協業品は、通常ラインとは異なる需要や評価軸があるため、背景を見て査定します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: ["sacai全般を、", "幅広く査定します。"],
      items: [
        {
          tag: "ITEM 01",
          title: "sacai レディース",
          text:
            "ワンピース、スカート、ニット、ジャケット、コートなど、ウィメンズの主要アイテムを幅広く査定します。",
        },
        {
          tag: "ITEM 02",
          title: "sacai メンズ",
          text:
            "ブルゾン、ジャケット、パンツ、ニット、シャツなど、構造性の強いメンズアイテムも積極的に見ます。",
        },
        {
          tag: "ITEM 03",
          title: "異素材切替・ドッキング系",
          text:
            "sacaiの代表性が強い切替ニット、ドッキングアウター、レイヤードスカートは特に丁寧に査定します。",
        },
        {
          tag: "ITEM 04",
          title: "コラボレーションアイテム",
          text:
            "sacai × WTAPS、sacai × NIKEなど、需要の強い協業品は内容に応じて個別評価します。",
        },
        {
          tag: "ITEM 05",
          title: "アウター・ジャケット",
          text:
            "MA-1、トレンチ、テーラード、ミリタリー由来のアウターなど、sacaiらしい構造の出るアイテムを見ます。",
        },
        {
          tag: "ITEM 06",
          title: "ニット・スウェット",
          text:
            "異素材の接続やレイヤード表現が分かりやすいトップス類も査定対象です。",
        },
        {
          tag: "ITEM 07",
          title: "スカート・ワンピース",
          text:
            "プリーツ、布帛、異素材を組み合わせたボトムスやドレスは、sacaiらしさが評価に結びつきやすい領域です。",
        },
        {
          tag: "ITEM 08",
          title: "バッグ・小物類",
          text:
            "アイテムにより査定可能です。コレクション性や市場需要を確認して個別にご案内します。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: ["高価買取につながりやすい、", "sacaiのアイテム。"],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のようなsacaiを丁寧に見ます。",
      items: [
        {
          index: "01",
          title: "異素材切替・ドッキング構造が際立つ代表作",
          text:
            "ニット、ジャケット、スカート、ワンピースなど、sacaiの設計思想が明確に表れたアイテムは評価対象です。",
        },
        {
          index: "02",
          title: "人気シーズンの象徴的なアイテム",
          text:
            "その時期のコレクション性を強く示すルックや、継続的に需要のあるアウター類は丁寧に査定します。",
        },
        {
          index: "03",
          title: "sacai × WTAPS、sacai × NIKEなどのコラボレーション",
          text:
            "ブランド同士の文脈が重なる協業品は、市場評価が通常ラインと異なる場合があるため、個別に価値を見ます。",
        },
        {
          index: "04",
          title: "状態良好なアーカイブ・入手困難品",
          text:
            "過去作の中でも流通量が少なく、現在も需要が続いているものは、年代だけでなく希少性まで含めて判断します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“人気ブランド”としてではなく、",
        "構造を持った服として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: ["一般的な査定で、", "見落とされやすいsacaiの価値。"],
      intro:
        "Enclopediaは、単純な型番相場だけでなく、そのアイテムがsacaiの中でどのような意味を持つかも確認します。",
      items: [
        {
          index: "01",
          title: "異素材切替を“装飾”として片付けない",
          text:
            "sacaiの切替や重なりは、ブランドの本質に関わる設計です。どの程度構造性が強いかを見ます。",
        },
        {
          index: "02",
          title: "コラボ品を一括評価しない",
          text:
            "コラボレーションは、相手ブランドや発売時期、現在の需要で評価が変わります。個別に確認します。",
        },
        {
          index: "03",
          title: "着用価値と再流通価値をあわせて見る",
          text:
            "Enclopediaはレンタル事業も行うため、次に着る人へ渡る価値があるかという視点も持って査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: ["LINE事前査定から、", "宅配買取へ。"],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。宅配買取へ進む前に、おおまかな査定可否をご案内します。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: ["相談しやすく、", "進めやすいsacai買取。"],
      intro:
        "LINEで相談してから進められるため、買取成立までの不安と手間を減らせます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、無駄な発送や手間を減らせます。",
        },
        {
          title: "送料は無料",
          text: "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: ["sacai買取で、", "よくあるご質問。"],
      items: [
        {
          question: "どのsacaiアイテムを買取していますか？",
          answer:
            "ジャケット、コート、ニット、スカート、ワンピース、パンツ、コラボレーション品など幅広くご相談いただけます。",
        },
        {
          question: "sacai × WTAPSやsacai × NIKEも査定できますか？",
          answer:
            "はい。コラボレーション背景や市場需要を確認し、通常ラインとは分けて個別に評価します。",
        },
        {
          question: "古いsacaiの方が高くなりますか？",
          answer:
            "年代だけで一律には判断しません。現在の需要、シーズン性、希少性、状態を総合して査定します。",
        },
        {
          question: "異素材切替のアイテムは評価されやすいですか？",
          answer:
            "sacaiらしさが強く表れるため注目して見ます。ただし、査定額は状態・需要・アイテムごとの流通性を踏まえて判断します。",
        },
        {
          question: "LINE査定をせずに宅配買取を申し込めますか？",
          answer:
            "申込自体は可能ですが、事前にLINE査定をご利用いただくことで、査定可否や大まかな方向性を確認しやすくなります。",
        },
        {
          question: "宅配キットは必ず使う必要がありますか？",
          answer:
            "必須ではありません。宅配キットは選択制のため、ご自身で梱包いただける場合はそのまま発送いただけます。",
        },
      ],
    },
    finalCta: {
      titleLines: ["まずはLINEで、", "sacaiの価値をご相談ください。"],
      body:
        "異素材切替、コラボレーション、シーズン背景まで確認。\nEnclopediaが、次の流通へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "maison-margiela",
    category: "BRAND BUYBACK",
    listTitle: "Maison Margiela買取",
    listDescription:
      "本人期・アーティザナル・定番名作まで、Maison Margielaの文脈と市場価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "メゾンマルジェラ買取｜本人期・アーティザナルまで査定するEnclopedia",
      description:
        "メゾンマルジェラの買取ならEnclopediaへ。Maison Margiela、旧Maison Martin Margiela、本人期、アーティザナル、八の字ライダース、Tabi、5ACなどを、シーズン背景・希少性・市場価値まで見て査定します。",
      keyword:
        "メゾンマルジェラ 買取,マルジェラ 買取,Maison Margiela 買取,Maison Martin Margiela 買取,マルタンマルジェラ 買取,メゾンマルジェラ 高価買取,マルジェラ 宅配買取,マルジェラ 本人期 買取,マルジェラ アーティザナル 買取,八の字ライダース 買取,Tabi 買取,5AC 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/maison-margiela",
    },
    hero: {
      label: "MAISON MARGIELA BUYBACK",
      number: "03 / BUYBACK",
      titleLines: [
        "メゾンマルジェラ買取。",
        "本人期から現行まで、価値を見て査定します。",
      ],
      body:
        "Enclopediaは、Maison Margielaをブランド名だけで一律に判断しません。\n旧Maison Martin Margiela期、本人期、アーティザナル、定番名作、近年の人気モデルまで、それぞれの文脈と市場価値を確認して査定します。\nまずはLINEで事前にご相談いただき、納得感のある宅配買取へつなげます。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "buyback",
        "archive valuation",
        "artisanal",
        "maison margiela",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "流行価格ではなく、",
        "その服の背景まで見る。",
      ],
      image: "media/archive_fashion/buyback3.png",
      imageAlt: "Maison Margiela買取のメイン画像",
    },
    thesis: {
      text:
        "Maison Margielaの価値は、定番人気だけでなく、制作年代・ライン・思想・希少性まで見て判断する。",
      keywords: ["period", "line", "rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "マルジェラの価値は、",
        "“有名な定番”だけでは決まらない。",
      ],
      body:
        "Maison Margielaは、解体、再構築、匿名性、既製服の転用といった視点から、ファッションの見方を変えてきたブランドです。\nTabiや5ACのような広く知られた名作がある一方で、旧Maison Martin Margiela期の衣服、本人期のアーカイブ、アーティザナル作品、各ラインの文脈を持つピースには、別の評価軸があります。\nEnclopediaでは、商品名だけではなく、そのアイテムがマルジェラの歴史の中でどの位置にあるかまで確認します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "年代・ディレクションを見る",
          text:
            "旧Maison Martin Margiela期、本人期、デザインチーム期、近年のMaison Margielaまで、制作年代と背景を踏まえて判断します。",
        },
        {
          number: "02",
          title: "ラインとアイテムの意味を見る",
          text:
            "0・1・4・6・10・14・22などのライン、アーティザナル、REPLICA、定番名作など、どの文脈で評価されるかを確認します。",
        },
        {
          number: "03",
          title: "現在の需要と希少性を見る",
          text:
            "八の字ライダース、Tabi、5AC、グラムスラムなどの定番需要に加え、流通量の少ないアーカイブや希少品も個別に見ます。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "Maison Margiela全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "旧Maison Martin Margiela",
          text:
            "マルタン本人期を含む旧名称時代の衣服・靴・バッグ・アクセサリーを丁寧に査定します。",
        },
        {
          tag: "ITEM 02",
          title: "本人期アーカイブ",
          text:
            "過去シーズンの象徴的なジャケット、コート、ニット、パンツ、再構築的なデザインなどを個別に見ます。",
        },
        {
          tag: "ITEM 03",
          title: "Artisanal / アーティザナル",
          text:
            "手仕事や再構成の価値が強く表れるアーティザナル作品は、一般的な型番査定に寄せず個別評価します。",
        },
        {
          tag: "ITEM 04",
          title: "八の字ライダース",
          text:
            "マルジェラを代表する定番名作。年代・素材・サイズ・状態を確認して査定します。",
        },
        {
          tag: "ITEM 05",
          title: "Tabi シューズ",
          text:
            "ブーツ、パンプス、バレエ、ローファーなど、ブランドを象徴するTabiラインを幅広く見ます。",
        },
        {
          tag: "ITEM 06",
          title: "5AC・グラムスラム・バッグ",
          text:
            "現在も需要の高いバッグ類は、型・サイズ・素材・状態を踏まえて査定します。",
        },
        {
          tag: "ITEM 07",
          title: "REPLICA・スニーカー",
          text:
            "ジャーマントレーナーを含むREPLICAシリーズやシューズ類もご相談いただけます。",
        },
        {
          tag: "ITEM 08",
          title: "MM6 Maison Margiela",
          text:
            "MM6についても、アイテム内容や需要に応じて査定対象として確認します。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "Maison Margielaのアイテム。",
      ],
      intro:
        "査定額は状態や相場で変動しますが、Enclopediaでは特に以下のようなマルジェラを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "本人期・旧Maison Martin Margiela期のアーカイブ",
          text:
            "現在では入手しにくい年代の作品や、ブランド史において重要性のあるピースは、一般的な古着評価ではなくアーカイブ価値まで見ます。",
        },
        {
          index: "02",
          title: "アーティザナル・再構築性の強い作品",
          text:
            "既製服の転用、再縫製、匿名性といったマルジェラらしい思想が明確に表れる作品は、流通性だけでなく文脈を確認します。",
        },
        {
          index: "03",
          title: "八の字ライダース・Tabi・5ACなどの定番名作",
          text:
            "広く需要があり、買取検索でも頻繁に対象となる名作は、状態・仕様・年代によって適切に査定します。",
        },
        {
          index: "04",
          title: "状態良好な希少サイズ・人気モデル",
          text:
            "人気サイズ、付属品完備、流通量の少ない仕様は、再販市場での需要を踏まえて評価します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“マルジェラだから高い”ではなく、",
        "なぜ評価されるのかまで見て査定する。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいマルジェラの価値。",
      ],
      intro:
        "Maison Margielaは、同じブランド名でも、年代・ライン・制作背景によって評価軸が大きく変わるブランドです。",
      items: [
        {
          index: "01",
          title: "旧名称期と現行品を一括で見ない",
          text:
            "Maison Martin Margiela期や本人期のアーカイブは、現行品とは異なる需要と評価軸があります。年代を確認して判断します。",
        },
        {
          index: "02",
          title: "ライン番号や作品背景を無視しない",
          text:
            "ラインごとに役割が異なるため、単なるカテゴリ分類ではなく、その服がどの位置づけにあるかを見ます。",
        },
        {
          index: "03",
          title: "定番人気とアーカイブ価値を分けて考える",
          text:
            "Tabiや5ACは市場需要が強い定番として、本人期やアーティザナルは文化的・希少性のあるアーカイブとして、それぞれ異なる角度で査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。宅配買取へ進む前に、おおまかな査定可否をご案内します。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいマルジェラ買取。",
      ],
      intro:
        "LINEで相談してから進められるため、買取成立までの不安と手間を減らせます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、無駄な発送や手間を減らせます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "Maison Margiela買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question: "どのMaison Margielaアイテムを買取していますか？",
          answer:
            "ジャケット、コート、ニット、パンツ、シャツ、靴、バッグ、アクセサリーなど幅広くご相談いただけます。",
        },
        {
          question: "Maison Martin Margiela期や本人期も査定できますか？",
          answer:
            "はい。旧名称期、本人期、アーカイブ品は特に年代や作品背景を確認して個別に査定します。",
        },
        {
          question: "アーティザナル作品も買取対象ですか？",
          answer:
            "はい。作品内容・状態・希少性を見て個別に判断します。写真付きのLINE事前査定がおすすめです。",
        },
        {
          question: "八の字ライダースやTabiも査定できますか？",
          answer:
            "はい。八の字ライダース、Tabiブーツ、Tabiパンプス、Tabiバレエなど、代表的な定番品も査定対象です。",
        },
        {
          question: "5ACやグラムスラムなどバッグも買取できますか？",
          answer:
            "はい。Maison Margielaのバッグ類も、型・サイズ・素材・状態を確認して査定します。",
        },
        {
          question: "MM6 Maison Margielaも査定できますか？",
          answer:
            "アイテム内容に応じて査定可能です。まずはLINEで写真をお送りください。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "Maison Margielaの価値をご相談ください。",
      ],
      body:
        "本人期、アーティザナル、定番名作まで確認。\nEnclopediaが、その一着の背景と再流通価値を丁寧に見ます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "comme-des-garcons-homme-plus",
    category: "BRAND BUYBACK",
    listTitle: "コムデギャルソンオムプリュス買取",
    listDescription:
      "ランウェイ品、縮絨・再構築・変形テーラードまで、COMME des GARCONS HOMME PLUSのコレクション価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "コムデギャルソンオムプリュス買取｜COMME des GARCONS HOMME PLUSを価値まで査定するEnclopedia",
      description:
        "COMME des GARCONS HOMME PLUS（コムデギャルソンオムプリュス）の買取ならEnclopediaへ。ランウェイ登場品、縮絨・再構築・変形テーラード、近年作から希少アーカイブまで、シーズン背景と需要を見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "コムデギャルソンオムプリュス 買取,コムデギャルソン オムプリュス 買取,COMME des GARCONS HOMME PLUS 買取,COMME des GARÇONS HOMME PLUS 買取,オムプリュス 買取,HOMME PLUS 買取,CDG HOMME PLUS 買取,コムデギャルソンオムプリュス 高価買取,コムデギャルソンオムプリュス 宅配買取,コムデギャルソンオムプリュス LINE査定,コムデギャルソンオムプリュス アーカイブ 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/comme-des-garcons-homme-plus",
    },
    hero: {
      label: "COMME DES GARÇONS HOMME PLUS BUYBACK",
      number: "04 / BUYBACK",
      titleLines: [
        "コムデギャルソンオムプリュス買取。",
        "コレクション価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、COMME des GARÇONS HOMME PLUSを、単なるブランド名や型番だけで判断しません。\nランウェイ登場品、シーズンテーマを象徴するデザイン、縮絨・再構築・変形テーラードなど、COMME des GARCONS HOMME PLUSならではの表現を確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "homme plus buyback",
        "runway archive",
        "collection valuation",
        "comme des garçons",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "相場だけではなく、",
        "コレクション文脈まで見る。",
      ],
      image: "media/archive_fashion/buyback4.png",
      imageAlt: "コムデギャルソンオムプリュス買取のメイン画像",
    },
    thesis: {
      text:
        "COMME des GARCONS HOMME PLUSの価値は、ブランド名ではなく、シーズン背景・造形・アーカイブ性まで見て判断する。",
      keywords: ["season", "runway", "archive rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "オムプリュスの価値は、",
        "“ギャルソンのメンズライン”では終わらない。",
      ],
      body:
        "COMME des GARÇONS HOMME PLUSは、川久保玲が手がけるメンズのコレクションラインとして、長年にわたりパリで発表されてきました。\nテーラリングの解体、身体の見え方を変える造形、極端な布量、縮絨や再構築、テーマ性の強いルックなど、COMME des GARCONS HOMME PLUSには通常のメンズウェアとは異なる評価軸があります。\nEnclopediaでは、単に“ギャルソンの服”としてではなく、その一着がどのシーズンに属し、どのような意味を持つかまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "シーズン背景とテーマを見る",
          text:
            "COMME des GARCONS HOMME PLUSはコレクションごとのテーマ性が強いラインです。どのシーズンに属し、どの思想や造形が表れているかを確認します。",
        },
        {
          number: "02",
          title: "ランウェイ性と造形を見る",
          text:
            "ランウェイ登場品、テーマを象徴するルック、変形テーラード、立体構造、再構築的なデザインは特に丁寧に評価します。",
        },
        {
          number: "03",
          title: "希少性と現在の需要を見る",
          text:
            "年代、流通量、サイズ、状態、国内外での需要を踏まえ、型番相場だけで終わらせず個別に判断します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "COMME des GARCONS HOMME PLUS全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "ランウェイ登場品・ルック採用品",
          text:
            "コレクションのテーマを強く反映するジャケット、コート、パンツ、セットアップなどを積極的に確認します。",
        },
        {
          tag: "ITEM 02",
          title: "縮絨・再構築・変形テーラード",
          text:
            "COMME des GARCONS HOMME PLUSらしさが明確に表れる加工、歪み、断ち切り、立体的な構造のあるアイテムを丁寧に見ます。",
        },
        {
          tag: "ITEM 03",
          title: "ジャケット・コート・ブルゾン",
          text:
            "テーラードジャケット、ロングコート、ライダース、ブルゾンなど、コレクション性の出やすいアウター類を査定します。",
        },
        {
          tag: "ITEM 04",
          title: "パンツ・サルエル・ワイドシルエット",
          text:
            "COMME des GARCONS HOMME PLUSの造形性が表れやすいパンツ類も対象。布量、シルエット、シーズン性まで見ます。",
        },
        {
          tag: "ITEM 05",
          title: "シャツ・ニット・カットソー",
          text:
            "総柄シャツ、変形シャツ、テーマ性の強いニットやカットソーも、年代や需要を踏まえて査定します。",
        },
        {
          tag: "ITEM 06",
          title: "シューズ・ブーツ・NIKEコラボ",
          text:
            "ブーツ、レザーシューズ、NIKEとのコラボレーションスニーカーなど、需要のあるフットウェアも確認します。",
        },
        {
          tag: "ITEM 07",
          title: "バッグ・バックパック・小物",
          text:
            "リュック、バッグ、アクセサリーなども、COMME des GARCONS HOMME PLUSとしての市場需要に応じて査定対象です。",
        },
        {
          tag: "ITEM 08",
          title: "80s〜近年作までのアーカイブ",
          text:
            "古い年代だから高いとは限りませんが、流通量が少なく、今も評価される作品はアーカイブ価値まで見ます。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "COMME des GARCONS HOMME PLUSのアイテム。",
      ],
      intro:
        "査定額は状態や相場で変動しますが、Enclopediaでは特に以下のようなCOMME des GARCONS HOMME PLUSを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "ランウェイ登場品・シーズンを象徴する作品",
          text:
            "特定シーズンのテーマを強く示す柄、装飾、シルエット、セットアップはコレクション性が評価されやすい領域です。",
        },
        {
          index: "02",
          title: "縮絨・再構築・変形などブランド性の強い造形",
          text:
            "一般的なメンズ服では見られない構造性があり、COMME des GARCONS HOMME PLUSらしさが際立つピースは丁寧に査定します。",
        },
        {
          index: "03",
          title: "近年の人気シーズン・需要の強いアウター",
          text:
            "新しいシーズンのコート、ジャケット、ブルゾンなどは、二次流通でも需要が維持されやすく、相場を確認して評価します。",
        },
        {
          index: "04",
          title: "希少アーカイブ・再入手しにくい過去作",
          text:
            "80年代〜2000年代の中でも、今なお評価されるルックや流通の少ない作品は、相場表では拾いきれない価値を見ます。",
        },
        {
          index: "05",
          title: "NIKEコラボ・人気シューズ・ブーツ",
          text:
            "COMME des GARCONS HOMME PLUS名義のコラボレーションやシューズ類も、型番・状態・現在需要に応じて査定します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“メンズ服”としてではなく、",
        "コレクションの一部として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいCOMME des GARCONS HOMME PLUSの価値。",
      ],
      intro:
        "COMME des GARCONS HOMME PLUSは、同じコムデギャルソンでも、評価軸が大きく異なるコレクションラインです。",
      items: [
        {
          index: "01",
          title: "通常のメンズラインと同列に扱わない",
          text:
            "COMME des GARCONS HOMME PLUSはパリコレクションを軸に展開される表現性の高いラインです。HOMMEやHOMME DEUXとは異なる基準で見ます。",
        },
        {
          index: "02",
          title: "型番と状態だけで終わらせない",
          text:
            "品番・コンディションに加え、シーズンテーマ、ルック性、装飾性、海外需要まで確認して判断します。",
        },
        {
          index: "03",
          title: "アーカイブとしての意味を査定に反映する",
          text:
            "Enclopediaはアーカイブのレンタル・共有を行うため、次に着る人へ渡す価値があるかという視点も持って査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。COMME des GARCONS HOMME PLUSの年代・品番・ディテールが分かる写真があると、より確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・市場性・アーカイブ価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいCOMME des GARCONS HOMME PLUS買取。",
      ],
      intro:
        "LINEで相談してから進められるため、宅配買取へ進む前に査定可否や方向性を確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、コレクション性の強いアイテムも安心してご相談いただけます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "COMME des GARCONS HOMME PLUS買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "COMME des GARÇONS HOMME PLUSなら、どのようなアイテムを買取していますか？",
          answer:
            "ジャケット、コート、ブルゾン、パンツ、シャツ、ニット、カットソー、ブーツ、スニーカー、バッグ、小物まで幅広くご相談いただけます。",
        },
        {
          question:
            "古いCOMME des GARCONS HOMME PLUSのアーカイブほど高く買い取ってもらえますか？",
          answer:
            "年代だけで一律には判断しません。過去作でもコレクション性・希少性・現在需要が高いものは丁寧に評価します。一方で近年シーズンの人気作が高値になりやすい場合もあります。",
        },
        {
          question:
            "ランウェイ登場品やシーズンを象徴する服は評価されますか？",
          answer:
            "はい。COMME des GARCONS HOMME PLUSはコレクション文脈が重要なラインのため、ランウェイ性やテーマを担うデザインは査定時に確認します。",
        },
        {
          question:
            "縮絨、再構築、変形テーラードなども査定できますか？",
          answer:
            "はい。COMME des GARCONS HOMME PLUSらしさが強く表れる造形や加工は、アイテムの魅力として丁寧に見ます。",
        },
        {
          question:
            "コムデギャルソンオムプリュスの買取相場を先に知りたいです。",
          answer:
            "状態やシーズン、需要によって異なるため、まずはLINEで写真をお送りください。宅配買取へ進む前に、おおまかな方向性をご案内します。",
        },
        {
          question:
            "LINE査定をせずに宅配買取を申し込めますか？",
          answer:
            "申込自体は可能ですが、事前にLINE査定をご利用いただくことで、査定可否や大まかな評価方針を確認しやすくなります。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "COMME des GARCONS HOMME PLUSの価値をご相談ください。",
      ],
      body:
        "ランウェイ品、アーカイブ、縮絨・再構築・変形デザインまで確認。\nEnclopediaが、コレクション価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "junya-watanabe",
    category: "BRAND BUYBACK",
    listTitle: "JUNYA WATANABE買取",
    listDescription:
      "MAN・eYe・コラボレーション作品まで、ジュンヤワタナベの構造性と市場価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "ジュンヤワタナベ買取｜JUNYA WATANABE MAN・eYe・コラボまで査定するEnclopedia",
      description:
        "JUNYA WATANABE（ジュンヤワタナベ）の買取ならEnclopediaへ。JUNYA WATANABE MAN、eYe JUNYA WATANABE MAN、コムデギャルソン期、パッチワーク・再構築・デニム・コラボレーション品まで、背景と需要を見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "ジュンヤワタナベ 買取,JUNYA WATANABE 買取,ジュンヤワタナベマン 買取,JUNYA WATANABE MAN 買取,eYe JUNYA WATANABE MAN 買取,アイ ジュンヤワタナベマン 買取,コムデギャルソン ジュンヤワタナベ 買取,ジュンヤワタナベ 高価買取,ジュンヤワタナベ 宅配買取,ジュンヤワタナベ LINE査定,ジュンヤワタナベ コラボ 買取,ジュンヤワタナベ アーカイブ 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/junya-watanabe",
    },
    hero: {
      label: "JUNYA WATANABE BUYBACK",
      number: "05 / BUYBACK",
      titleLines: [
        "ジュンヤワタナベ買取。",
        "構造と背景まで見て査定します。",
      ],
      body:
        "Enclopediaは、JUNYA WATANABEをブランド名だけで一律に判断しません。\nJUNYA WATANABE MAN、eYe JUNYA WATANABE MAN、コムデギャルソン期の作品を含め、パッチワーク、再構築、異素材ミックス、デニム、コラボレーション性まで確認して査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "buyback",
        "reconstruction",
        "collaboration value",
        "junya watanabe",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "人気だけでなく、",
        "服の構造まで見る。",
      ],
      image: "media/archive_fashion/buyback5.png",
      imageAlt: "JUNYA WATANABE買取のメイン画像",
    },
    thesis: {
      text:
        "JUNYA WATANABEの価値は、ブランド名だけでなく、造形・コラボレーション・アーカイブ性まで見て判断する。",
      keywords: ["construction", "collaboration", "rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "ジュンヤワタナベの価値は、",
        "“コラボ人気”だけでは測れない。",
      ],
      body:
        "JUNYA WATANABEは、既存の服や素材に独自の解釈を加え、まったく異なる存在へ変えるデザインで評価されてきたブランドです。\nパッチワーク、再構築、デニム、立体的なカッティング、ワークやミリタリーを基盤にしたメンズウェア、そして歴史あるブランドとのコラボレーション。\nEnclopediaでは、単に人気モデルかどうかではなく、その一着がどの時期に生まれ、どのような表現を持つかまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "造形と再構築性を見る",
          text:
            "パッチワーク、異素材の接続、立体裁断、解体的な切替など、JUNYA WATANABEらしい構造がどの程度表れているかを確認します。",
        },
        {
          number: "02",
          title: "ラインと年代を見る",
          text:
            "JUNYA WATANABE、JUNYA WATANABE MAN、eYe JUNYA WATANABE MAN、コムデギャルソン表記時代など、ラインと年代の違いを踏まえて判断します。",
        },
        {
          number: "03",
          title: "コラボレーション価値を見る",
          text:
            "Levi’s、THE NORTH FACE、Moncler、LOEWE、Tricker’s、New Balanceなどとの協業品は、市場需要と作品背景を確認して個別に評価します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "JUNYA WATANABE全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "JUNYA WATANABE",
          text:
            "ウィメンズを中心とした本ライン。立体構造、変形ドレス、ジャケット、スカート、素材表現が強い作品を丁寧に査定します。",
        },
        {
          tag: "ITEM 02",
          title: "JUNYA WATANABE MAN",
          text:
            "ワーク、ミリタリー、テーラード、デニムを基盤にしたメンズライン。再構築性やコラボレーション性を見て判断します。",
        },
        {
          tag: "ITEM 03",
          title: "eYe JUNYA WATANABE MAN",
          text:
            "定番性と実用性の中にJUNYAらしさが表れるライン。ジャケット、パンツ、アウター、コラボ品まで査定対象です。",
        },
        {
          tag: "ITEM 04",
          title: "パッチワーク・再構築アイテム",
          text:
            "JUNYA WATANABEを象徴するパッチワークデニム、再構築ジャケット、切替コートなどは特に丁寧に確認します。",
        },
        {
          tag: "ITEM 05",
          title: "デニム・レザー・ワーク系",
          text:
            "デニムパンツ、デニムジャケット、ライダース、ワークジャケット、ミリタリー由来のアウターなどを幅広く見ます。",
        },
        {
          tag: "ITEM 06",
          title: "コラボレーション作品",
          text:
            "Levi’s、THE NORTH FACE、Moncler、LOEWE、Tricker’s、New Balanceなど、注目度の高い協業品を個別に評価します。",
        },
        {
          tag: "ITEM 07",
          title: "シューズ・バッグ・アクセサリー",
          text:
            "スニーカー、ブーツ、バッグ、小物類も、アイテム内容と市場需要に応じて査定可能です。",
        },
        {
          tag: "ITEM 08",
          title: "過去シーズンのアーカイブ",
          text:
            "年代が古いだけでなく、今も評価される作品や流通量の少ないピースはアーカイブ価値まで見ます。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "JUNYA WATANABEのアイテム。",
      ],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のようなJUNYA WATANABEを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "パッチワーク・再構築・立体造形が際立つ作品",
          text:
            "JUNYA WATANABEのデザイン性が強く現れるジャケット、パンツ、ドレス、コートは、単なる型番相場ではなく造形そのものを見ます。",
        },
        {
          index: "02",
          title: "JUNYA WATANABE MANの人気アウター・デニム",
          text:
            "メンズラインのコート、ブルゾン、ワークジャケット、パッチワークデニムは、中古市場でも評価されやすい領域です。",
        },
        {
          index: "03",
          title: "Levi’s・THE NORTH FACEなどのコラボレーション",
          text:
            "歴史あるブランドとの協業品は、通常ラインとは異なる需要を持つため、発売時期・仕様・現在需要を踏まえて個別に見ます。",
        },
        {
          index: "04",
          title: "eYe JUNYA WATANABE MANの人気モデル",
          text:
            "実用性がありながらデザイン性も高いeYeラインは、ジャケット、パンツ、アウターを中心に査定対象として確認します。",
        },
        {
          index: "05",
          title: "希少アーカイブ・入手困難品",
          text:
            "過去シーズンの中でも、コレクター需要が続く作品や流通量が限られるピースは、アーカイブ性を含めて判断します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“人気のコラボ”としてではなく、",
        "JUNYAの設計思想が宿る服として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいJUNYAの価値。",
      ],
      intro:
        "JUNYA WATANABEは、ライン・年代・コラボレーション背景によって評価軸が大きく変わるブランドです。",
      items: [
        {
          index: "01",
          title: "JUNYA WATANABE MAN・eYe・ウィメンズを一括で見ない",
          text:
            "同じJUNYA WATANABEでも、本ライン、JUNYA WATANABE MAN、eYeでは需要の層と評価軸が異なります。ラインごとの性格を踏まえて査定します。",
        },
        {
          index: "02",
          title: "コラボ品をロゴ人気だけで判断しない",
          text:
            "相手ブランドの知名度だけでなく、JUNYA側がどのように再解釈したか、シーズン性と再流通需要まで見ます。",
        },
        {
          index: "03",
          title: "造形の強さを査定に反映する",
          text:
            "パッチワーク、再構築、異素材ミックス、独自カッティングなど、JUNYAらしさが強い服は、作品性も踏まえて確認します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。ブランドタグ、品番、コラボ先、ディテールが分かる写真があると確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・市場性・アーカイブ性を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいJUNYA WATANABE買取。",
      ],
      intro:
        "LINEで相談してから進められるため、コラボ品やアーカイブ作品も宅配買取へ進む前に確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、状態や市場性に不安がある品も事前に確認できます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "JUNYA WATANABE買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "どのJUNYA WATANABEを買取していますか？",
          answer:
            "JUNYA WATANABE、JUNYA WATANABE MAN、eYe JUNYA WATANABE MANを中心に、ジャケット、コート、デニム、パンツ、ドレス、ニット、バッグ、シューズまで幅広くご相談いただけます。",
        },
        {
          question:
            "ジュンヤワタナベマンのアイテムも査定できますか？",
          answer:
            "はい。JUNYA WATANABE MANラインのアウター、デニム、ワークウェア、コラボレーション品は特に需要を確認しながら査定します。",
        },
        {
          question:
            "eYe JUNYA WATANABE MANも買取対象ですか？",
          answer:
            "はい。eYeラインも査定対象です。ジャケット、パンツ、アウター、コラボ品などを個別に確認します。",
        },
        {
          question:
            "Levi’sやTHE NORTH FACEとのコラボも高く売れますか？",
          answer:
            "状態・型・年代・現在需要によって異なりますが、JUNYA WATANABEのコラボレーション品は中古市場でも注目度が高く、個別に評価します。",
        },
        {
          question:
            "パッチワークや再構築デザインは査定で見てもらえますか？",
          answer:
            "はい。JUNYA WATANABEの代表的な表現として、造形性・希少性・需要を確認して査定します。",
        },
        {
          question:
            "買取相場を先に知りたいです。",
          answer:
            "まずはLINEで写真をお送りください。アイテムのライン、状態、コラボ有無、需要を確認し、宅配買取へ進む前に大まかな方向性をご案内します。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "JUNYA WATANABEの価値をご相談ください。",
      ],
      body:
        "JUNYA WATANABE MAN・eYe・コラボレーション品、パッチワークや再構築まで確認。\nEnclopediaが、服の背景と再流通価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "noir-kei-ninomiya",
    category: "BRAND BUYBACK",
    listTitle: "NOIR KEI NINOMIYA買取",
    listDescription:
      "チュール、ハーネス、構築的ドレス、アーカイブ作品まで、ノワールケイニノミヤの造形価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "NOIR KEI NINOMIYA買取｜ノワールケイニノミヤを価値まで査定するEnclopedia",
      description:
        "NOIR KEI NINOMIYA（ノワールケイニノミヤ）の買取ならEnclopediaへ。チュール、オーガンジー、フリル、ハーネス、ドレス、スカート、構築的ジャケットなどを、シーズン背景・造形性・希少性まで見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "NOIR KEI NINOMIYA 買取,noir kei ninomiya 買取,ノワールケイニノミヤ 買取,ノワール ケイ ニノミヤ 買取,コムデギャルソン ノワールケイニノミヤ 買取,NOIR KEI NINOMIYA 高価買取,ノワールケイニノミヤ 高価買取,NOIR KEI NINOMIYA 宅配買取,ノワールケイニノミヤ 宅配買取,NOIR KEI NINOMIYA LINE査定,ノワールケイニノミヤ 買取相場,NOIR KEI NINOMIYA アーカイブ 買取,チュールドレス 買取,ハーネス 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/noir-kei-ninomiya",
    },
    hero: {
      label: "NOIR KEI NINOMIYA BUYBACK",
      number: "06 / BUYBACK",
      titleLines: [
        "NOIR KEI NINOMIYA買取。",
        "造形と技法の価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、NOIR KEI NINOMIYAをブランド名だけで一律に判断しません。\nチュール、オーガンジー、フリル、ハーネス、レーザーカット、構築的ドレスなど、ノワールケイニノミヤならではの素材表現と造形性を確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "buyback",
        "sculptural construction",
        "tulle dress",
        "noir kei ninomiya",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "装飾ではなく、",
        "造形としての価値を見る。",
      ],
      image: "media/archive_fashion/buyback6.png",
      imageAlt: "NOIR KEI NINOMIYA買取のメイン画像",
    },
    thesis: {
      text:
        "NOIR KEI NINOMIYAの価値は、人気だけでなく、素材の接続・構築性・シーズン背景まで見て判断する。",
      keywords: ["construction", "material", "rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "ノワールケイニノミヤの価値は、",
        "“黒い服”の一言では終わらない。",
      ],
      body:
        "NOIR KEI NINOMIYAは、黒を軸にしながら、素材・技法・立体構造によって服の表情を拡張してきたブランドです。\nチュールやオーガンジー、フリル、リボン、ハーネス状の構成、糸を使わない接続、レーザーカット的な造形。\nEnclopediaでは、単に人気モデルかどうかではなく、その一着がどのような技術と造形で成立しているか、どの時期の表現かまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "素材表現と構築性を見る",
          text:
            "チュール、オーガンジー、フリル、レース、ハーネス、パーツ接続など、NOIR KEI NINOMIYAらしい立体的な造形を確認します。",
        },
        {
          number: "02",
          title: "シーズン背景と作品性を見る",
          text:
            "近年作か過去作かだけでなく、コレクションの中でどのような位置を持つアイテムかを見ながら判断します。",
        },
        {
          number: "03",
          title: "希少性と現在需要を見る",
          text:
            "流通量、サイズ、保存状態、人気デザイン、国内外の再販需要を踏まえ、個別に査定します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "NOIR KEI NINOMIYA全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "チュール・オーガンジー系ドレス",
          text:
            "ブランドを象徴する立体的なドレス、透け感のあるレイヤード、ボリューム表現の強い作品を査定します。",
        },
        {
          tag: "ITEM 02",
          title: "フリル・リボン・レース装飾",
          text:
            "甘さではなく構築性として装飾を扱うアイテムを、デザインの密度と需要を踏まえて評価します。",
        },
        {
          tag: "ITEM 03",
          title: "ハーネス・チェーン・立体パーツ",
          text:
            "ハーネスベスト、チェーン接続、パーツ構成が特徴的なアイテムは、NOIRらしさが強く出る領域です。",
        },
        {
          tag: "ITEM 04",
          title: "ジャケット・コート・MA-1",
          text:
            "チュール重ね、異素材切替、装飾性の高いアウター類は、状態と希少性を確認して査定します。",
        },
        {
          tag: "ITEM 05",
          title: "スカート・パンツ・ボトムス",
          text:
            "フリルスカート、プリーツスカート、ボリュームパンツなど、構造性のあるボトムスも対象です。",
        },
        {
          tag: "ITEM 06",
          title: "ブラウス・トップス・カットソー",
          text:
            "フリルブラウス、チュールトップス、特殊素材使いのTシャツやカットソーも査定可能です。",
        },
        {
          tag: "ITEM 07",
          title: "アクセサリー・ハーネス小物",
          text:
            "衣服の延長としてデザインされた装飾小物やハーネス系アイテムも内容に応じて見ます。",
        },
        {
          tag: "ITEM 08",
          title: "過去シーズンのアーカイブ",
          text:
            "古いだけで高くなるわけではありませんが、現在も評価される造形性の強い作品はアーカイブ価値まで見ます。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "NOIR KEI NINOMIYAのアイテム。",
      ],
      intro:
        "査定額は状態や相場で変動しますが、Enclopediaでは特に以下のようなノワールケイニノミヤを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "チュール・オーガンジーを用いたドレスやワンピース",
          text:
            "ブランドの魅力が最も強く出やすい領域です。ボリューム、構築性、装飾密度、保存状態を総合して査定します。",
        },
        {
          index: "02",
          title: "ハーネス・チェーン・レーザーカット的構造を持つ作品",
          text:
            "NOIR KEI NINOMIYAらしい技法と造形が明確に現れる作品は、単なる衣類としてではなくデザイン価値を見ます。",
        },
        {
          index: "03",
          title: "フリルスカート・構築的ブラウス・立体ジャケット",
          text:
            "中古市場でも検索されやすい人気カテゴリ。需要が継続している品は丁寧に評価します。",
        },
        {
          index: "04",
          title: "近年シーズンの人気作・市場流通が少ない品",
          text:
            "現行人気が高い作品や、流通量が限られる希少作は、現在の再販需要を踏まえて判断します。",
        },
        {
          index: "05",
          title: "保存状態の良いアーカイブ作品",
          text:
            "繊細な素材を使うブランドだからこそ、チュール破れ、パーツ欠け、装飾状態を確認したうえで価値を見ます。",
        },
      ],
    },
    quote: {
      textLines: [
        "“可愛い装飾”としてではなく、",
        "黒を構築する服として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいNOIRの価値。",
      ],
      intro:
        "NOIR KEI NINOMIYAは、素材の繊細さと造形の強さが同時に存在するため、一般的なブランド古着査定だけでは価値が平準化されやすいブランドです。",
      items: [
        {
          index: "01",
          title: "“装飾が多い服”として片付けない",
          text:
            "フリルやチュールは単なる装飾ではなく、服の骨格を形づくる要素です。造形としてどの程度完成度が高いかを見ます。",
        },
        {
          index: "02",
          title: "素材の特殊性を査定に反映する",
          text:
            "チュール、オーガンジー、メッシュ、硬質パーツなど、一般的な服とは異なる保存性・希少性を確認します。",
        },
        {
          index: "03",
          title: "アーカイブ性と次の着用価値を見る",
          text:
            "Enclopediaはアーカイブのレンタル・共有を行うため、次の着用者へ渡る価値があるかという視点も持って査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。素材感、全体像、ブランドタグ、装飾部分が分かる写真があると確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。繊細な装飾やチュールがある場合は、なるべく形を崩さない梱包をおすすめします。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・需要・造形価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいNOIR KEI NINOMIYA買取。",
      ],
      intro:
        "LINEで相談してから進められるため、繊細な素材や特殊構造のアイテムも、宅配買取へ進む前に確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、特殊構造の服も安心してご相談いただけます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "NOIR KEI NINOMIYA買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "どのNOIR KEI NINOMIYAアイテムを買取していますか？",
          answer:
            "ドレス、ワンピース、スカート、ブラウス、トップス、ジャケット、コート、MA-1、ハーネスベスト、装飾小物など幅広くご相談いただけます。",
        },
        {
          question:
            "チュールやオーガンジーのドレスも査定できますか？",
          answer:
            "はい。NOIR KEI NINOMIYAを代表する重要なカテゴリとして、状態・造形性・需要を確認して査定します。",
        },
        {
          question:
            "ハーネスやチェーンを使った特殊なアイテムも買取対象ですか？",
          answer:
            "はい。ハーネス、チェーン、立体パーツ、レーザーカット的な構造を持つ作品も、ブランド性を踏まえて確認します。",
        },
        {
          question:
            "ノワールケイニノミヤの買取相場を先に知りたいです。",
          answer:
            "状態やシーズン、デザイン、需要によって異なるため、まずはLINEで写真をお送りください。宅配買取へ進む前に、おおまかな方向性をご案内します。",
        },
        {
          question:
            "古いNOIR KEI NINOMIYAのアーカイブも査定できますか？",
          answer:
            "はい。年代だけで一律には判断せず、現在の需要、希少性、造形性を踏まえて個別に評価します。",
        },
        {
          question:
            "LINE査定をせずに宅配買取を申し込めますか？",
          answer:
            "申込自体は可能ですが、事前にLINE査定をご利用いただくことで、査定可否や大まかな評価方針を確認しやすくなります。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "NOIR KEI NINOMIYAの価値をご相談ください。",
      ],
      body:
        "チュール、ハーネス、構築的ドレス、アーカイブ作品まで確認。\nEnclopediaが、造形の価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "raf-simons",
    category: "BRAND BUYBACK",
    listTitle: "RAF SIMONS買取",
    listDescription:
      "宗教期・テロ期・Consumed・Virginia Creeperなどのアーカイブから、近年作・コラボレーションまで、RAF SIMONSの文脈と市場価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "RAF SIMONS買取｜ラフシモンズのアーカイブ価値まで査定するEnclopedia",
      description:
        "RAF SIMONS（ラフシモンズ）の買取ならEnclopediaへ。宗教期、テロ期、Consumed、Virginia Creeper、Riot Riot Riotなどの希少アーカイブから、コート、ニット、シャツ、スウェット、バッグ、スニーカー、コラボレーション品まで、シーズン背景・希少性・市場価値を見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "RAF SIMONS 買取,ラフシモンズ 買取,RAF SIMONS 高価買取,ラフシモンズ 高価買取,RAF SIMONS 宅配買取,ラフシモンズ 宅配買取,RAF SIMONS LINE査定,ラフシモンズ 買取相場,RAF SIMONS アーカイブ 買取,ラフシモンズ アーカイブ 買取,RAF SIMONS 宗教期 買取,RAF SIMONS テロ期 買取,RAF SIMONS Consumed 買取,RAF SIMONS Virginia Creeper 買取,RAF SIMONS Riot Riot Riot 買取,RAF SIMONS Sterling Ruby 買取,RAF SIMONS Eastpak 買取,RAF SIMONS adidas 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/raf-simons",
    },
    hero: {
      label: "RAF SIMONS BUYBACK",
      number: "07 / BUYBACK",
      titleLines: [
        "RAF SIMONS買取。",
        "アーカイブの文脈まで見て査定します。",
      ],
      body:
        "Enclopediaは、RAF SIMONSをブランド名や相場だけで一律に判断しません。\n宗教期、テロ期、Consumed、Virginia Creeper、Riot Riot Riotなどのアーカイブ性、グラフィックの希少性、シーズン背景、現在の市場需要まで確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "raf simons buyback",
        "archive valuation",
        "youth culture",
        "collection rarity",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "相場だけではなく、",
        "時代性と熱量まで見る。",
      ],
      image: "media/archive_fashion/buyback7.png",
      imageAlt: "RAF SIMONS買取のメイン画像",
    },
    thesis: {
      text:
        "RAF SIMONSの価値は、ブランド名ではなく、シーズン背景・グラフィック・希少性・カルチャー性まで見て判断する。",
      keywords: ["season", "graphic", "archive rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "RAF SIMONSの価値は、",
        "“古着の相場”だけでは測れない。",
      ],
      body:
        "RAF SIMONSは、ユースカルチャー、音楽、アート、反抗性、テーラリングを結びつけ、メンズファッションの見え方を大きく変えてきたブランドです。\n同じRAF SIMONSでも、初期アーカイブ、グラフィックアイテム、テーラード、ニット、コラボレーション、近年作では評価軸が異なります。\nEnclopediaでは、単に人気モデルかどうかではなく、その一着がどのシーズンに属し、どのような文脈と希少性を持つかまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "シーズン背景を見る",
          text:
            "宗教期、テロ期、Consumed、Virginia Creeper、Riot Riot Riotなど、シーズンごとの評価軸やコレクション文脈を確認します。",
        },
        {
          number: "02",
          title: "グラフィックと象徴性を見る",
          text:
            "プリント、パッチ、スローガン、アートワーク、シルエットなど、そのアイテムがRAF SIMONSらしさをどの程度示しているかを見ます。",
        },
        {
          number: "03",
          title: "希少性と現在需要を見る",
          text:
            "流通量、サイズ、状態、国内外の需要、再入手難度を踏まえ、型番相場だけで終わらせず個別に判断します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "RAF SIMONS全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "初期・名作シーズンのアーカイブ",
          text:
            "90年代後半から2000年代の希少アーカイブ、宗教期、テロ期、Consumed、Virginia Creeperなどの重要ピースを丁寧に査定します。",
        },
        {
          tag: "ITEM 02",
          title: "グラフィックTシャツ・スウェット",
          text:
            "RAF SIMONSを象徴するプリント、パッチ、スローガン、フォトグラフィックな表現があるアイテムを確認します。",
        },
        {
          tag: "ITEM 03",
          title: "ニット・オーバーサイズトップス",
          text:
            "ビッグシルエット、ダメージ加工、パッチワーク、グラフィック性の強いニットやセーターを査定します。",
        },
        {
          tag: "ITEM 04",
          title: "コート・ジャケット・テーラード",
          text:
            "モッズコート、ボンバージャケット、テーラードジャケット、ロングコートなど、シルエットとシーズン性を見て判断します。",
        },
        {
          tag: "ITEM 05",
          title: "シャツ・パンツ・セットアップ",
          text:
            "RAF SIMONSらしい細身・オーバーサイズ・制服的なムードを持つシャツやパンツ、セットアップも対象です。",
        },
        {
          tag: "ITEM 06",
          title: "Sterling Ruby・アート系コラボ",
          text:
            "RAF SIMONS × Sterling Rubyなど、アート文脈を持つ協業品は、通常ラインとは異なる評価軸で確認します。",
        },
        {
          tag: "ITEM 07",
          title: "Eastpak・adidas・Dr. Martens等のコラボ",
          text:
            "バッグ、スニーカー、ブーツなどのコラボレーション品も、型・状態・現在需要を踏まえて査定します。",
        },
        {
          tag: "ITEM 08",
          title: "近年作・終了前後のコレクション",
          text:
            "近年の人気シーズンやブランド終了前後の作品も、需要と希少性を確認して個別に評価します。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "RAF SIMONSのアイテム。",
      ],
      intro:
        "査定額は状態や相場で変動しますが、Enclopediaでは特に以下のようなRAF SIMONSを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "宗教期・テロ期・Consumedなどの希少アーカイブ",
          text:
            "RAF SIMONSの歴史の中でも評価されやすいシーズンの作品は、年代・状態・流通量・国内外需要を踏まえて個別に見ます。",
        },
        {
          index: "02",
          title: "強いグラフィックやスローガンを持つアイテム",
          text:
            "Tシャツ、スウェット、ニット、コートなど、視覚的な象徴性が強いピースは、単なる古着ではなくコレクション性を確認します。",
        },
        {
          index: "03",
          title: "ランウェイ性の高いコート・ジャケット・ニット",
          text:
            "シーズンテーマを担うシルエット、加工、素材使いのあるアイテムは、RAF SIMONSらしさとして査定に反映します。",
        },
        {
          index: "04",
          title: "Sterling RubyやEastpakなどのコラボレーション",
          text:
            "相手ブランドやアーティストの文脈、発売時期、現在の市場需要を確認し、通常ラインとは分けて評価します。",
        },
        {
          index: "05",
          title: "状態良好な希少サイズ・付属品完備品",
          text:
            "アーカイブ品はコンディション差が価格に影響しやすいため、保存状態、タグ、付属品、サイズ需要まで見て判断します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“高いブランド”としてではなく、",
        "時代をまとった服として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいRAF SIMONSの価値。",
      ],
      intro:
        "RAF SIMONSは、年代・シーズン・グラフィック・カルチャー背景によって評価が大きく変わるブランドです。",
      items: [
        {
          index: "01",
          title: "古いRAF SIMONSを一括で見ない",
          text:
            "アーカイブと呼ばれる年代でも、シーズンやアイテム内容によって需要は大きく異なります。単に古いかどうかではなく、作品性を確認します。",
        },
        {
          index: "02",
          title: "プリントやパッチを“柄”として片付けない",
          text:
            "RAF SIMONSのグラフィックは、シーズンテーマやカルチャー性と結びつくことがあります。視覚的な強さと文脈を見ます。",
        },
        {
          index: "03",
          title: "国内外のアーカイブ需要を踏まえる",
          text:
            "RAF SIMONSは海外評価も強いブランドです。日本国内の相場だけでなく、現在の再流通需要まで含めて査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。全体、ブランドタグ、品番タグ、プリントやパッチ、ダメージ箇所が分かる写真があると確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・市場性・アーカイブ価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいRAF SIMONS買取。",
      ],
      intro:
        "LINEで相談してから進められるため、アーカイブ品や相場が分かりにくいアイテムも、宅配買取へ進む前に確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、希少アーカイブも査定可否や方向性を事前に確認できます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "RAF SIMONS買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "どのRAF SIMONSアイテムを買取していますか？",
          answer:
            "ジャケット、コート、ニット、シャツ、Tシャツ、スウェット、パンツ、バッグ、スニーカー、ブーツ、コラボレーション品まで幅広くご相談いただけます。",
        },
        {
          question:
            "宗教期・テロ期・Consumedなどのアーカイブも査定できますか？",
          answer:
            "はい。RAF SIMONSの重要シーズンや希少アーカイブは、年代・状態・流通量・現在需要を確認して個別に査定します。",
        },
        {
          question:
            "古いRAF SIMONSほど高く買い取ってもらえますか？",
          answer:
            "年代だけで一律には判断しません。古い作品でも需要や希少性が高いものは評価されやすい一方、近年作でも人気モデルや状態の良い品は高値につながる場合があります。",
        },
        {
          question:
            "グラフィックTシャツやスウェットも買取対象ですか？",
          answer:
            "はい。RAF SIMONSらしいプリント、パッチ、スローガン、シーズン性のあるアイテムは丁寧に確認します。",
        },
        {
          question:
            "Sterling RubyやEastpakなどのコラボ品も査定できますか？",
          answer:
            "はい。コラボレーション背景、発売時期、状態、現在の市場需要を踏まえて個別に評価します。",
        },
        {
          question:
            "RAF SIMONSの買取相場を先に知りたいです。",
          answer:
            "まずはLINEで写真をお送りください。シーズン、品番、状態、需要を確認し、宅配買取へ進む前に大まかな方向性をご案内します。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "RAF SIMONSの価値をご相談ください。",
      ],
      body:
        "希少アーカイブ、グラフィック、コラボレーション、近年作まで確認。\nEnclopediaが、服の背景と再流通価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "rick-owens",
    category: "BRAND BUYBACK",
    listTitle: "Rick Owens買取",
    listDescription:
      "レザー、ジオバスケット、DRKSHDW、アウター、ブーツ、アーカイブ作品まで、Rick Owensの造形性と市場価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "Rick Owens買取｜リックオウエンスのアーカイブ価値まで査定するEnclopedia",
      description:
        "Rick Owens（リックオウエンス）の買取ならEnclopediaへ。レザージャケット、ジオバスケット、ラモーンズ、DRKSHDW、コート、パンツ、ブーツ、スニーカー、アーカイブ作品まで、シーズン背景・造形性・希少性・市場価値を見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "Rick Owens 買取,リックオウエンス 買取,Rick Owens 高価買取,リックオウエンス 高価買取,Rick Owens 宅配買取,リックオウエンス 宅配買取,Rick Owens LINE査定,リックオウエンス 買取相場,Rick Owens アーカイブ 買取,リックオウエンス アーカイブ 買取,DRKSHDW 買取,ダークシャドウ 買取,Rick Owens レザー 買取,Rick Owens ジオバスケット 買取,Rick Owens ラモーンズ 買取,Rick Owens ブーツ 買取,Rick Owens スニーカー 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/rick-owens",
    },
    hero: {
      label: "RICK OWENS BUYBACK",
      number: "08 / BUYBACK",
      titleLines: [
        "Rick Owens買取。",
        "造形と空気感まで見て査定します。",
      ],
      body:
        "Enclopediaは、Rick Owensをブランド名や型番だけで一律に判断しません。\nレザー、ドレープ、ロングシルエット、サルエルパンツ、ブーツ、ジオバスケット、ラモーンズ、DRKSHDWまで、Rick Owensならではの造形性・シーズン背景・現在の需要を確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "rick owens buyback",
        "archive valuation",
        "gothic minimalism",
        "sculptural silhouette",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "黒い服としてではなく、",
        "輪郭を変える服として見る。",
      ],
      image: "media/archive_fashion/buyback8.png",
      imageAlt: "Rick Owens買取のメイン画像",
    },
    thesis: {
      text:
        "Rick Owensの価値は、ブランド名だけでなく、シルエット・素材・シーズン性・身体の見え方まで見て判断する。",
      keywords: ["silhouette", "leather", "archive rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "Rick Owensの価値は、",
        "“黒くてモード”だけでは測れない。",
      ],
      body:
        "Rick Owensは、退廃的なムード、彫刻的なシルエット、レザーやジャージーの重さ、身体のラインを変えるパターンで独自の世界を築いてきたブランドです。\n同じ黒い服でも、レザー、ドレープ、ロング丈、サルエル、ブーツ、スニーカー、DRKSHDWでは評価軸が異なります。\nEnclopediaでは、単に人気モデルかどうかではなく、その一着がRick Owensの中でどのような輪郭と価値を持つかまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "シルエットと造形を見る",
          text:
            "ロング丈、ドレープ、サルエル、極端なボリューム、鋭い肩線など、Rick Owensらしい身体の見え方を確認します。",
        },
        {
          number: "02",
          title: "素材と経年の状態を見る",
          text:
            "レザー、ムートン、ジャージー、ナイロン、デニムなど、素材ごとの状態差や経年変化を踏まえて査定します。",
        },
        {
          number: "03",
          title: "定番需要と希少性を見る",
          text:
            "ジオバスケット、ラモーンズ、ブーツなどの定番需要に加え、流通量の少ないアーカイブやシーズン品も個別に判断します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "Rick Owens全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "Rick Owens メインライン",
          text:
            "レザージャケット、コート、ニット、カットソー、パンツ、ドレス、ブーツなど、メインラインの世界観が強い品を査定します。",
        },
        {
          tag: "ITEM 02",
          title: "DRKSHDW / ダークシャドウ",
          text:
            "デニム、スウェット、フーディー、カットソー、ラモーンズなど、日常性とRick Owensらしさを併せ持つラインも対象です。",
        },
        {
          tag: "ITEM 03",
          title: "レザー・ムートン・アウター",
          text:
            "リックを象徴するレザー、ムートン、ロングコート、ボンバー、ダウン、ジャケット類を丁寧に確認します。",
        },
        {
          tag: "ITEM 04",
          title: "ジオバスケット・ラモーンズ",
          text:
            "Rick Owensを代表するスニーカー類。サイズ、状態、カラー、素材、ソール減り、箱や付属品の有無まで見ます。",
        },
        {
          tag: "ITEM 05",
          title: "ブーツ・サンダル・シューズ",
          text:
            "レースアップブーツ、サイドジップブーツ、キスブーツ、サンダルなど、足元で世界観を作るアイテムも査定対象です。",
        },
        {
          tag: "ITEM 06",
          title: "サルエル・カーゴ・パンツ類",
          text:
            "股上の深いパンツ、カーゴパンツ、ドロップクロッチ、ワイドシルエットなど、造形性のあるボトムスを見ます。",
        },
        {
          tag: "ITEM 07",
          title: "バッグ・アクセサリー",
          text:
            "バッグ、ベルト、ウォレット、ジュエリー、小物類も、アイテム内容と市場需要に応じて査定します。",
        },
        {
          tag: "ITEM 08",
          title: "過去シーズンのアーカイブ",
          text:
            "古いだけで高くなるわけではありませんが、現在も評価されるシーズン品や流通量の少ない作品はアーカイブ価値まで見ます。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "Rick Owensのアイテム。",
      ],
      intro:
        "査定額は状態や相場で変動しますが、Enclopediaでは特に以下のようなRick Owensを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "レザー・ムートン・構築的アウター",
          text:
            "Rick Owensの世界観が強く出るアウター類は、素材の状態、シルエット、年代、現在需要を踏まえて査定します。",
        },
        {
          index: "02",
          title: "ジオバスケット・ラモーンズ・人気スニーカー",
          text:
            "定番として需要が強いシューズ類は、サイズ、カラー、ソールの減り、付属品、状態によって評価が変わります。",
        },
        {
          index: "03",
          title: "ブーツ・キスブーツ・存在感のあるフットウェア",
          text:
            "Rick Owensらしい重さと造形が出るブーツ類は、コーディネートの核になるアイテムとして丁寧に確認します。",
        },
        {
          index: "04",
          title: "サルエルパンツ・カーゴパンツ・ロング丈トップス",
          text:
            "身体のバランスを変えるシルエットを持つアイテムは、Rick Owensの着用価値として査定に反映します。",
        },
        {
          index: "05",
          title: "DRKSHDWの人気モデル・状態良好品",
          text:
            "DRKSHDWは流通量が比較的多い一方、人気モデルや状態の良い品、需要の強いサイズは評価対象として確認します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“黒い服”としてではなく、",
        "身体の輪郭を変える服として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいRick Owensの価値。",
      ],
      intro:
        "Rick Owensは、同じ黒・同じ定番でも、素材、シルエット、年代、サイズ需要によって評価が大きく変わるブランドです。",
      items: [
        {
          index: "01",
          title: "黒い服を一括で見ない",
          text:
            "Rick Owensの黒は、素材の重さ、落ち感、身体との距離で印象が変わります。単なるカラーではなく、造形として確認します。",
        },
        {
          index: "02",
          title: "定番品を型番だけで終わらせない",
          text:
            "ジオバスケットやラモーンズも、素材、カラー、サイズ、状態、付属品、現在需要で評価が変わります。",
        },
        {
          index: "03",
          title: "着用価値とアーカイブ性をあわせて見る",
          text:
            "Enclopediaはアーカイブのレンタル・共有を行うため、次の着用者へ渡る価値があるかという視点も持って査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。全体、ブランドタグ、品番タグ、素材表記、ダメージ箇所、シューズの場合はソール状態が分かる写真があると確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。レザーやブーツなど重量のあるアイテムも、ご自身の箱または選択制の宅配キットでお送りいただけます。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・市場性・アーカイブ価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいRick Owens買取。",
      ],
      intro:
        "LINEで相談してから進められるため、レザー、ブーツ、アーカイブ品、相場が分かりにくいアイテムも、宅配買取へ進む前に確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、状態や市場性に不安がある品も事前に確認できます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "Rick Owens買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "どのRick Owensアイテムを買取していますか？",
          answer:
            "レザージャケット、コート、ニット、カットソー、パンツ、ブーツ、スニーカー、バッグ、アクセサリー、DRKSHDWまで幅広くご相談いただけます。",
        },
        {
          question:
            "DRKSHDWも査定できますか？",
          answer:
            "はい。DRKSHDWのデニム、フーディー、カットソー、パンツ、ラモーンズなども査定対象です。状態や需要を確認して個別に判断します。",
        },
        {
          question:
            "ジオバスケットやラモーンズも買取対象ですか？",
          answer:
            "はい。Rick Owensを代表するスニーカーとして、サイズ、カラー、素材、ソールの減り、箱や付属品の有無まで確認します。",
        },
        {
          question:
            "レザーやブーツは高く売れますか？",
          answer:
            "状態・モデル・サイズ・年代・現在需要によって変わりますが、Rick Owensらしさが強いレザーやブーツは丁寧に評価します。",
        },
        {
          question:
            "古いRick Owensのアーカイブも査定できますか？",
          answer:
            "はい。年代だけで一律には判断せず、シーズン性、希少性、状態、現在の需要を踏まえて個別に査定します。",
        },
        {
          question:
            "Rick Owensの買取相場を先に知りたいです。",
          answer:
            "まずはLINEで写真をお送りください。モデル、品番、状態、サイズ、付属品、現在需要を確認し、宅配買取へ進む前に大まかな方向性をご案内します。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "Rick Owensの価値をご相談ください。",
      ],
      body:
        "レザー、ブーツ、ジオバスケット、ラモーンズ、DRKSHDW、アーカイブ作品まで確認。\nEnclopediaが、造形と再流通価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "issey-miyake",
    category: "BRAND BUYBACK",
    listTitle: "ISSEY MIYAKE買取",
    listDescription:
      "PLEATS PLEASE、HOMME PLISSÉ、ISSEY MIYAKE MEN、A-POC、BAO BAO、アーカイブ作品まで、イッセイミヤケの技法と市場価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "ISSEY MIYAKE買取｜イッセイミヤケのアーカイブ価値まで査定するEnclopedia",
      description:
        "ISSEY MIYAKE（イッセイミヤケ）の買取ならEnclopediaへ。PLEATS PLEASE、HOMME PLISSÉ ISSEY MIYAKE、ISSEY MIYAKE MEN、A-POC、BAO BAO、132 5.、アーカイブ作品まで、プリーツ技法・シーズン背景・希少性・市場価値を見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "ISSEY MIYAKE 買取,イッセイミヤケ 買取,ISSEY MIYAKE 高価買取,イッセイミヤケ 高価買取,ISSEY MIYAKE 宅配買取,イッセイミヤケ 宅配買取,ISSEY MIYAKE LINE査定,イッセイミヤケ 買取相場,ISSEY MIYAKE アーカイブ 買取,イッセイミヤケ アーカイブ 買取,PLEATS PLEASE 買取,プリーツプリーズ 買取,HOMME PLISSÉ 買取,オムプリッセ 買取,ISSEY MIYAKE MEN 買取,イッセイミヤケメン 買取,A-POC 買取,BAO BAO 買取,バオバオ 買取,132 5. ISSEY MIYAKE 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/issey-miyake",
    },
    hero: {
      label: "ISSEY MIYAKE BUYBACK",
      number: "09 / BUYBACK",
      titleLines: [
        "ISSEY MIYAKE買取。",
        "技法とアーカイブ価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、ISSEY MIYAKEをブランド名や型番だけで一律に判断しません。\nPLEATS PLEASE、HOMME PLISSÉ、ISSEY MIYAKE MEN、A-POC、BAO BAO、132 5.など、ラインごとの特性、プリーツ技法、素材、シルエット、シーズン背景、現在の需要まで確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "issey miyake buyback",
        "pleats valuation",
        "archive clothing",
        "technical design",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "服の形だけではなく、",
        "技法と思想まで見る。",
      ],
      image: "media/archive_fashion/buyback9.png",
      imageAlt: "ISSEY MIYAKE買取のメイン画像",
    },
    thesis: {
      text:
        "ISSEY MIYAKEの価値は、ブランド名だけでなく、プリーツ技法・素材開発・構造・保存状態・ラインごとの需要まで見て判断する。",
      keywords: ["pleats", "material innovation", "archive rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "ISSEY MIYAKEの価値は、",
        "“プリーツの服”だけでは終わらない。",
      ],
      body:
        "ISSEY MIYAKEは、身体と布の関係を技術から変えてきたブランドです。\n一枚の布、プリーツ、折り、伸縮、軽さ、機能性、量産と造形の両立。PLEATS PLEASEやHOMME PLISSÉのような定番ラインがある一方で、ISSEY MIYAKE MEN、A-POC、132 5.、過去シーズンのアーカイブには、それぞれ異なる評価軸があります。\nEnclopediaでは、単に人気ラインかどうかではなく、その一着がどの技法・ライン・時代背景の中にあるかまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "技法と素材を見る",
          text:
            "プリーツ、A-POC、折り構造、伸縮性、軽量素材、特殊加工など、ISSEY MIYAKEらしい技術的価値を確認します。",
        },
        {
          number: "02",
          title: "ラインと需要を見る",
          text:
            "PLEATS PLEASE、HOMME PLISSÉ、ISSEY MIYAKE MEN、BAO BAOなど、ラインごとの市場需要と評価軸を分けて判断します。",
        },
        {
          number: "03",
          title: "状態と希少性を見る",
          text:
            "プリーツの伸び、汚れ、引っ掛け、保管状態、サイズ、色柄、流通量、再入手難度を踏まえて個別に査定します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "ISSEY MIYAKE全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "PLEATS PLEASE ISSEY MIYAKE",
          text:
            "ワンピース、トップス、パンツ、スカート、カーディガン、セットアップなど、需要の高いプリーツプリーズを査定します。",
        },
        {
          tag: "ITEM 02",
          title: "HOMME PLISSÉ ISSEY MIYAKE",
          text:
            "ジャケット、パンツ、トップス、コート、セットアップなど、メンズで人気の高いオムプリッセを確認します。",
        },
        {
          tag: "ITEM 03",
          title: "ISSEY MIYAKE MEN",
          text:
            "ジャケット、コート、パンツ、ニット、シャツなど、廃止ラインを含むメンズアーカイブも丁寧に見ます。",
        },
        {
          tag: "ITEM 04",
          title: "ISSEY MIYAKE / メインライン",
          text:
            "ウィメンズのドレス、コート、トップス、パンツ、造形性の強いコレクションピースを査定します。",
        },
        {
          tag: "ITEM 05",
          title: "A-POC / 132 5. ISSEY MIYAKE",
          text:
            "一枚の布、折り、構造、プロダクト性が強いラインは、技法と希少性を踏まえて個別に確認します。",
        },
        {
          tag: "ITEM 06",
          title: "BAO BAO ISSEY MIYAKE",
          text:
            "トート、ショルダー、クラッチ、限定色、人気モデルなど、状態と需要を見て査定します。",
        },
        {
          tag: "ITEM 07",
          title: "me ISSEY MIYAKE / HaaT",
          text:
            "日常性の高いラインも、柄、素材、状態、現在需要に応じて査定対象として確認します。",
        },
        {
          tag: "ITEM 08",
          title: "過去シーズンのアーカイブ",
          text:
            "古いだけで高くなるわけではありませんが、現在も評価される技法・柄・シルエットを持つ作品はアーカイブ価値まで見ます。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "ISSEY MIYAKEのアイテム。",
      ],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のようなISSEY MIYAKEを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "PLEATS PLEASEの人気柄・セットアップ・ワンピース",
          text:
            "プリーツプリーズは中古市場でも検索需要が高いラインです。柄、色、サイズ、プリーツ状態、セットアップ可否を確認します。",
        },
        {
          index: "02",
          title: "HOMME PLISSÉのジャケット・パンツ・セットアップ",
          text:
            "オムプリッセは日常で着やすく需要が強いため、定番パンツ、ジャケット、セットアップ、人気色は丁寧に査定します。",
        },
        {
          index: "03",
          title: "ISSEY MIYAKE MENの希少アーカイブ",
          text:
            "廃止ラインであるISSEY MIYAKE MENは、年代・素材・シルエット・保存状態によって価値が変わります。",
        },
        {
          index: "04",
          title: "技法や構造が際立つメインラインの作品",
          text:
            "折り、立体構造、特殊素材、プリーツ以外の技術表現が強いコレクションピースは、単なる古着ではなく作品性を確認します。",
        },
        {
          index: "05",
          title: "BAO BAOの人気モデル・限定色・状態良好品",
          text:
            "BAO BAOは使用感や角擦れ、パーツ状態が査定に影響します。人気モデルや限定カラーは需要を踏まえて確認します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“着やすい服”としてではなく、",
        "布と身体を変える技法として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいISSEY MIYAKEの価値。",
      ],
      intro:
        "ISSEY MIYAKEは、同じブランド名でも、ライン・技法・素材・保存状態によって評価が大きく変わるブランドです。",
      items: [
        {
          index: "01",
          title: "プリーツを一括で見ない",
          text:
            "PLEATS PLEASEとHOMME PLISSÉでは需要層も評価軸も異なります。柄、色、形、サイズ、状態まで分けて確認します。",
        },
        {
          index: "02",
          title: "技法を“デザイン”だけで終わらせない",
          text:
            "A-POCや132 5.のように、作り方そのものが価値になるラインは、プロダクトとしての背景まで見ます。",
        },
        {
          index: "03",
          title: "状態と次の着用価値を重視する",
          text:
            "プリーツの伸び、シミ、引っ掛け、型崩れなどを確認しながら、次の着用者へ渡る価値があるかという視点で査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。全体、ブランドタグ、品番タグ、素材表記、プリーツ状態、汚れや引っ掛け箇所が分かる写真があると確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。ご自身の箱または選択制の宅配キットで商品をお送りください。プリーツやバッグはなるべく形を崩さない梱包をおすすめします。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・市場性・アーカイブ価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいISSEY MIYAKE買取。",
      ],
      intro:
        "LINEで相談してから進められるため、プリーツの状態やライン判別に不安があるアイテムも、宅配買取へ進む前に確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、状態や市場性に不安がある品も事前に確認できます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "ISSEY MIYAKE買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "どのISSEY MIYAKEアイテムを買取していますか？",
          answer:
            "PLEATS PLEASE、HOMME PLISSÉ、ISSEY MIYAKE MEN、メインライン、A-POC、132 5.、BAO BAO、me ISSEY MIYAKE、HaaTなど幅広くご相談いただけます。",
        },
        {
          question:
            "PLEATS PLEASEは高く売れますか？",
          answer:
            "柄、色、形、サイズ、プリーツ状態、現在需要によって変動します。ワンピース、セットアップ、人気柄、状態の良い品は丁寧に評価します。",
        },
        {
          question:
            "HOMME PLISSÉも査定できますか？",
          answer:
            "はい。HOMME PLISSÉのパンツ、ジャケット、トップス、セットアップ、コートなども査定対象です。定番需要と状態を確認して判断します。",
        },
        {
          question:
            "ISSEY MIYAKE MENの古いアーカイブも買取できますか？",
          answer:
            "はい。ISSEY MIYAKE MENは廃止ラインのため、年代、素材、シルエット、希少性、現在需要を踏まえて個別に査定します。",
        },
        {
          question:
            "BAO BAOのバッグも買取対象ですか？",
          answer:
            "はい。BAO BAOはモデル、色、サイズ、角擦れ、パーツ状態、付属品の有無を確認して査定します。",
        },
        {
          question:
            "ISSEY MIYAKEの買取相場を先に知りたいです。",
          answer:
            "まずはLINEで写真をお送りください。ライン、品番、状態、サイズ、プリーツやバッグの状態、現在需要を確認し、宅配買取へ進む前に大まかな方向性をご案内します。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "ISSEY MIYAKEの価値をご相談ください。",
      ],
      body:
        "PLEATS PLEASE、HOMME PLISSÉ、ISSEY MIYAKE MEN、A-POC、BAO BAO、アーカイブ作品まで確認。\nEnclopediaが、技法と再流通価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "yohji-yamamoto",
    category: "BRAND BUYBACK",
    listTitle: "YOHJI YAMAMOTO買取",
    listDescription:
      "YOHJI YAMAMOTO POUR HOMME、Y's、Ground Y、S'YTE、アーカイブ作品まで、ヨウジヤマモトの黒・ドレープ・カッティングの価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "YOHJI YAMAMOTO買取｜ヨウジヤマモトのアーカイブ価値まで査定するEnclopedia",
      description:
        "YOHJI YAMAMOTO（ヨウジヤマモト）の買取ならEnclopediaへ。YOHJI YAMAMOTO POUR HOMME、Y's、Ground Y、S'YTE、ジャケット、コート、パンツ、シャツ、ドレープ、ウールギャバ、アーカイブ作品まで、シーズン背景・造形性・希少性・市場価値を見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "YOHJI YAMAMOTO 買取,ヨウジヤマモト 買取,Yohji Yamamoto 買取,ヨウジヤマモト 高価買取,YOHJI YAMAMOTO 高価買取,YOHJI YAMAMOTO 宅配買取,ヨウジヤマモト 宅配買取,YOHJI YAMAMOTO LINE査定,ヨウジヤマモト 買取相場,YOHJI YAMAMOTO アーカイブ 買取,ヨウジヤマモト アーカイブ 買取,YOHJI YAMAMOTO POUR HOMME 買取,ヨウジヤマモト プールオム 買取,Y's 買取,ワイズ 買取,Y's for men 買取,Ground Y 買取,グラウンドワイ 買取,S'YTE 買取,サイト 買取,ウールギャバ 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/yohji-yamamoto",
    },
    hero: {
      label: "YOHJI YAMAMOTO BUYBACK",
      number: "10 / BUYBACK",
      titleLines: [
        "YOHJI YAMAMOTO買取。",
        "黒と余白の価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、YOHJI YAMAMOTOをブランド名や型番だけで一律に判断しません。\nYOHJI YAMAMOTO POUR HOMME、Y's、Ground Y、S'YTE、過去シーズンのアーカイブまで、黒、ドレープ、カッティング、ウールギャバ、シルエット、シーズン背景、現在の需要を確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "yohji yamamoto buyback",
        "black tailoring",
        "archive valuation",
        "drape silhouette",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "黒い服としてではなく、",
        "余白をまとう服として見る。",
      ],
      image: "media/archive_fashion/buyback10.png",
      imageAlt: "YOHJI YAMAMOTO買取のメイン画像",
    },
    thesis: {
      text:
        "YOHJI YAMAMOTOの価値は、ブランド名だけでなく、黒・布量・ドレープ・カッティング・シーズン背景まで見て判断する。",
      keywords: ["black", "drape", "archive rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "YOHJI YAMAMOTOの価値は、",
        "“黒い服”だけでは測れない。",
      ],
      body:
        "YOHJI YAMAMOTOは、黒、余白、非対称、布量、ドレープ、身体との距離感によって、服の見え方そのものを変えてきたブランドです。\n同じ黒のジャケットでも、ウールギャバの落ち感、肩線、着丈、分量、シーズンテーマによって価値は大きく変わります。\nEnclopediaでは、単にヨウジヤマモトというブランド名だけでなく、その一着がどのラインに属し、どのような造形と時代性を持つかまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "黒と素材の表情を見る",
          text:
            "ウールギャバ、リネン、レーヨン、コットン、レザーなど、素材ごとの落ち感・重さ・経年状態を確認します。",
        },
        {
          number: "02",
          title: "シルエットとカッティングを見る",
          text:
            "ロング丈、ワイドパンツ、ドレープ、アシンメトリー、変形パターンなど、YOHJI YAMAMOTOらしい身体との距離感を見ます。",
        },
        {
          number: "03",
          title: "ラインと希少性を見る",
          text:
            "POUR HOMME、Y's、Y's for men、Ground Y、S'YTEなど、ラインごとの需要とアーカイブ性を分けて判断します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "YOHJI YAMAMOTO全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "YOHJI YAMAMOTO POUR HOMME",
          text:
            "ジャケット、コート、パンツ、シャツ、ニット、セットアップなど、メンズラインのアーカイブ性が強い品を査定します。",
        },
        {
          tag: "ITEM 02",
          title: "YOHJI YAMAMOTO / Femme",
          text:
            "ドレス、ジャケット、コート、スカート、パンツなど、ウィメンズの造形性が高い作品も丁寧に見ます。",
        },
        {
          tag: "ITEM 03",
          title: "Y's / Y's for men",
          text:
            "日常性とヨウジらしい余白を持つY's、廃止ラインを含むY's for menも、年代と需要を踏まえて査定します。",
        },
        {
          tag: "ITEM 04",
          title: "Ground Y / S'YTE",
          text:
            "比較的新しいラインも査定対象です。人気型、状態、サイズ、現在需要を確認して個別に判断します。",
        },
        {
          tag: "ITEM 05",
          title: "ウールギャバ・定番ジャケット",
          text:
            "ヨウジヤマモトを象徴するウールギャバのジャケット、コート、パンツ、セットアップを丁寧に確認します。",
        },
        {
          tag: "ITEM 06",
          title: "ワイドパンツ・サルエル・袴パンツ",
          text:
            "布量、落ち感、シルエットの美しさが価値に直結しやすいボトムス類も査定対象です。",
        },
        {
          tag: "ITEM 07",
          title: "プリント・刺繍・メッセージ系アイテム",
          text:
            "シーズン性の強いプリント、刺繍、手書き風グラフィック、メッセージ性のある作品は個別に価値を見ます。",
        },
        {
          tag: "ITEM 08",
          title: "過去シーズンのアーカイブ",
          text:
            "古いだけで高くなるわけではありませんが、現在も評価されるシルエットや希少性のある作品はアーカイブ価値まで見ます。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "YOHJI YAMAMOTOのアイテム。",
      ],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のようなYOHJI YAMAMOTOを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "POUR HOMMEのジャケット・コート・セットアップ",
          text:
            "YOHJI YAMAMOTO POUR HOMMEの中でも、シルエットが美しいテーラード、ロングコート、セットアップは需要を確認して評価します。",
        },
        {
          index: "02",
          title: "ウールギャバを用いた定番・名作アイテム",
          text:
            "ヨウジヤマモトらしい落ち感と黒の深さが出るウールギャバのアイテムは、状態・年代・型を丁寧に見ます。",
        },
        {
          index: "03",
          title: "ワイドパンツ・袴パンツ・サルエルパンツ",
          text:
            "身体のバランスを変えるボトムスは、ヨウジらしさが強く、サイズや状態次第で評価につながりやすい領域です。",
        },
        {
          index: "04",
          title: "シーズンテーマを象徴するプリント・刺繍作品",
          text:
            "一見扱いが難しいグラフィックやメッセージ性のある作品も、コレクション文脈と需要を見て個別に判断します。",
        },
        {
          index: "05",
          title: "Y's for menなどの希少アーカイブ",
          text:
            "廃止ラインや流通量の少ない過去作は、年代だけでなく、現在も着用価値があるかまで確認します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“黒い服”としてではなく、",
        "余白と身体の関係を変える服として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいYOHJI YAMAMOTOの価値。",
      ],
      intro:
        "YOHJI YAMAMOTOは、同じ黒・同じジャケットでも、素材、分量、カッティング、年代、ラインによって評価が大きく変わるブランドです。",
      items: [
        {
          index: "01",
          title: "黒い服を一括で見ない",
          text:
            "ヨウジの黒は、素材、落ち感、陰影、布量によって印象が変わります。単なる色ではなく、造形として確認します。",
        },
        {
          index: "02",
          title: "定番品を型番だけで終わらせない",
          text:
            "ジャケット、パンツ、コートも、肩線、着丈、分量、素材、シーズン性によって価値が変わります。",
        },
        {
          index: "03",
          title: "着用価値とアーカイブ性をあわせて見る",
          text:
            "Enclopediaはアーカイブのレンタル・共有を行うため、次の着用者へ渡る価値があるかという視点も持って査定します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。全体、ブランドタグ、品番タグ、素材表記、汚れやダメージ箇所が分かる写真があると確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。コートやセットアップなど重量のあるアイテムも、ご自身の箱または選択制の宅配キットでお送りいただけます。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・市場性・アーカイブ価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいYOHJI YAMAMOTO買取。",
      ],
      intro:
        "LINEで相談してから進められるため、アーカイブ品や相場が分かりにくいアイテムも、宅配買取へ進む前に確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、状態や市場性に不安がある品も事前に確認できます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "YOHJI YAMAMOTO買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "どのYOHJI YAMAMOTOアイテムを買取していますか？",
          answer:
            "YOHJI YAMAMOTO POUR HOMME、YOHJI YAMAMOTO、Y's、Y's for men、Ground Y、S'YTEを中心に、ジャケット、コート、パンツ、シャツ、ニット、セットアップ、バッグ、小物まで幅広くご相談いただけます。",
        },
        {
          question:
            "YOHJI YAMAMOTO POUR HOMMEは高く売れますか？",
          answer:
            "状態・年代・素材・シルエット・現在需要によって変動します。特にジャケット、コート、セットアップ、ワイドパンツなどは丁寧に評価します。",
        },
        {
          question:
            "Y'sやY's for menも査定できますか？",
          answer:
            "はい。Y's、Y's for menも査定対象です。廃止ラインや希少な過去作は、年代・状態・現在需要を踏まえて個別に確認します。",
        },
        {
          question:
            "Ground YやS'YTEも買取対象ですか？",
          answer:
            "はい。Ground Y、S'YTEも査定可能です。人気型、サイズ、状態、再販需要を確認して判断します。",
        },
        {
          question:
            "古いYOHJI YAMAMOTOのアーカイブも買取できますか？",
          answer:
            "はい。年代だけで一律には判断せず、シーズン背景、希少性、素材、シルエット、現在の需要を踏まえて個別に査定します。",
        },
        {
          question:
            "YOHJI YAMAMOTOの買取相場を先に知りたいです。",
          answer:
            "まずはLINEで写真をお送りください。ライン、品番、状態、サイズ、素材、現在需要を確認し、宅配買取へ進む前に大まかな方向性をご案内します。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "YOHJI YAMAMOTOの価値をご相談ください。",
      ],
      body:
        "POUR HOMME、Y's、Ground Y、S'YTE、ウールギャバ、ワイドパンツ、アーカイブ作品まで確認。\nEnclopediaが、黒と余白の価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
  {
    slug: "tao-comme-des-garcons",
    category: "BRAND BUYBACK",
    listTitle: "tao COMME des GARÇONS買取",
    listDescription:
      "tao、tricot COMME des GARÇONS、フリル・レース・刺繍・パッチワーク・過去シーズンのアーカイブ作品まで、tao COMME des GARÇONSの繊細な造形価値を見て査定する専用買取ページ。",
    seo: {
      title:
        "tao COMME des GARÇONS買取｜タオ コムデギャルソンの価値まで査定するEnclopedia",
      description:
        "tao COMME des GARÇONS（タオ コムデギャルソン）の買取ならEnclopediaへ。tao、tricot COMME des GARÇONS、フリル、レース、刺繍、パッチワーク、ワンピース、ブラウス、スカート、ジャケット、アーカイブ作品まで、シーズン背景・造形性・希少性・市場価値を見て査定。LINE事前査定から全国対応の宅配買取へご案内します。",
      keyword:
        "tao COMME des GARCONS 買取,tao COMME des GARÇONS 買取,タオ コムデギャルソン 買取,tao 買取,タオ 買取,tao 高価買取,タオ 高価買取,tao 宅配買取,タオ 宅配買取,tao LINE査定,タオ 買取相場,tricot COMME des GARCONS 買取,tricot COMME des GARÇONS 買取,トリコ コムデギャルソン 買取,コムデギャルソン タオ 買取,コムデギャルソン レディース 買取,tao アーカイブ 買取,フリル ワンピース 買取,レース ブラウス 買取,アーカイブ買取,enclopedia,アンクロペディア",
      canonicalPath: "/brand_buyback/tao-comme-des-garcons",
    },
    hero: {
      label: "TAO COMME DES GARÇONS BUYBACK",
      number: "11 / BUYBACK",
      titleLines: [
        "tao COMME des GARÇONS買取。",
        "繊細な造形価値まで見て査定します。",
      ],
      body:
        "Enclopediaは、tao COMME des GARÇONSをブランド名や型番だけで一律に判断しません。\nフリル、レース、刺繍、パッチワーク、ワンピース、ブラウス、スカート、ジャケット、tricot COMME des GARÇONS時代の作品まで、素材表現・シーズン背景・現在の需要を確認し、個々のアイテムにフォーカスして査定します。\nまずはLINEで無料の事前査定。納得いただけた方を、全国対応の宅配買取へご案内します。",
      primaryCtaLabel: "LINEで事前査定する",
      primaryCtaHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryCtaLabel: "宅配買取を申し込む",
      secondaryCtaHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
      keywords: [
        "tao buyback",
        "tricot archive",
        "lace and frill",
        "comme des garçons",
      ],
      visualLabel: "ARCHIVE VALUATION",
      visualTitle: [
        "可愛い服としてではなく、",
        "構築された繊細さとして見る。",
      ],
      image: "media/archive_fashion/buyback11.png",
      imageAlt: "tao COMME des GARÇONS買取のメイン画像",
    },
    thesis: {
      text:
        "tao COMME des GARÇONSの価値は、ブランド名だけでなく、素材表現・装飾密度・構築性・シーズン背景まで見て判断する。",
      keywords: ["lace", "frill", "archive rarity"],
    },
    intro: {
      label: "02 / OUR BUYBACK VIEW",
      titleLines: [
        "tao COMME des GARÇONSの価値は、",
        "“かわいい服”だけでは測れない。",
      ],
      body:
        "tao COMME des GARÇONSは、フリル、レース、刺繍、パッチワーク、ギャザー、丸みのあるシルエットなどを通じて、柔らかさと構築性を同時に成立させるブランドです。\n甘さのある表現の中にも、COMME des GARÇONSらしい違和感、余白、素材の重なり、身体との距離があります。\nEnclopediaでは、単に人気アイテムかどうかではなく、その一着がtaoの中でどのような素材表現と時代性を持つかまで確認して査定します。",
    },
    valuation: {
      label: "03 / WHAT WE VALUE",
      titleLines: [
        "Enclopediaが重視する、",
        "3つの査定視点。",
      ],
      items: [
        {
          number: "01",
          title: "素材表現を見る",
          text:
            "レース、チュール、コットン、ウール、刺繍、パッチワークなど、taoらしい素材の重なりと表情を確認します。",
        },
        {
          number: "02",
          title: "装飾と構造を見る",
          text:
            "フリル、ギャザー、切替、ボリューム、丸みのあるシルエットが、単なる装飾ではなく服の構造として成立しているかを見ます。",
        },
        {
          number: "03",
          title: "ラインと希少性を見る",
          text:
            "tao、tricot COMME des GARÇONS、過去シーズンのアーカイブなど、ライン・年代・現在需要を分けて判断します。",
        },
      ],
    },
    targetLines: {
      label: "04 / TARGET ITEMS",
      titleLines: [
        "tao COMME des GARÇONS全般を、",
        "幅広く査定します。",
      ],
      items: [
        {
          tag: "ITEM 01",
          title: "tao COMME des GARÇONS",
          text:
            "現行のtaoライン。ワンピース、ブラウス、スカート、パンツ、ジャケット、コートなどを幅広く査定します。",
        },
        {
          tag: "ITEM 02",
          title: "tricot COMME des GARÇONS",
          text:
            "tricot時代の作品も対象です。年代、素材、柄、シルエット、現在需要を踏まえて丁寧に確認します。",
        },
        {
          tag: "ITEM 03",
          title: "ワンピース・ドレス",
          text:
            "フリル、レース、ギャザー、切替、ボリューム感が出やすいカテゴリとして、特に丁寧に見ます。",
        },
        {
          tag: "ITEM 04",
          title: "ブラウス・シャツ",
          text:
            "襟、袖、刺繍、レース、フリルのディテールが強いブラウスやシャツは、taoらしさが評価につながりやすい領域です。",
        },
        {
          tag: "ITEM 05",
          title: "スカート・パンツ",
          text:
            "ボリュームスカート、パッチワーク、チェック、レース使い、丸みのあるパンツなど、造形性のあるボトムスも査定対象です。",
        },
        {
          tag: "ITEM 06",
          title: "ジャケット・コート",
          text:
            "素材切替、装飾、丸いフォルム、COMME des GARÇONSらしい違和感を持つアウター類を確認します。",
        },
        {
          tag: "ITEM 07",
          title: "ニット・カットソー",
          text:
            "日常着に近いアイテムでも、柄、刺繍、素材感、シーズン性によって価値が変わるため個別に見ます。",
        },
        {
          tag: "ITEM 08",
          title: "過去シーズンのアーカイブ",
          text:
            "古いだけで高くなるわけではありませんが、現在も評価される素材表現や希少性のある作品はアーカイブ価値まで見ます。",
        },
      ],
    },
    highValueItems: {
      label: "05 / HIGH VALUE ITEMS",
      titleLines: [
        "高価買取につながりやすい、",
        "tao COMME des GARÇONSのアイテム。",
      ],
      intro:
        "査定額は状態や市場性で変動しますが、Enclopediaでは特に以下のようなtao COMME des GARÇONSを丁寧に確認します。",
      items: [
        {
          index: "01",
          title: "フリル・レース・刺繍が印象的なワンピース",
          text:
            "taoらしい繊細さと造形性が強く出るワンピースやドレスは、状態・素材・シーズン性を踏まえて評価します。",
        },
        {
          index: "02",
          title: "tricot COMME des GARÇONS時代の希少アーカイブ",
          text:
            "過去のtricot作品は、年代、柄、素材、現在需要によって価値が変わります。古さだけでなく着用価値まで確認します。",
        },
        {
          index: "03",
          title: "パッチワーク・チェック・花柄などの象徴的な作品",
          text:
            "視覚的な強さがあり、taoやtricotの世界観が伝わる柄物・切替アイテムは個別に査定します。",
        },
        {
          index: "04",
          title: "保存状態の良いブラウス・スカート・ジャケット",
          text:
            "白や淡色、繊細な素材は状態差が査定に影響します。シミ、黄ばみ、ほつれ、レース破れまで確認します。",
        },
        {
          index: "05",
          title: "近年シーズンの人気作・流通量の少ない品",
          text:
            "現行taoの人気アイテムや、市場で見つかりにくいサイズ・柄・デザインは現在需要を踏まえて判断します。",
        },
      ],
    },
    quote: {
      textLines: [
        "“甘い服”としてではなく、",
        "違和感を含んだ繊細な構造として見る。",
      ],
    },
    assessmentDifference: {
      label: "06 / ASSESSMENT DIFFERENCE",
      titleLines: [
        "一般的な査定で、",
        "見落とされやすいtaoの価値。",
      ],
      intro:
        "tao COMME des GARÇONSは、装飾性が高い一方で、素材や構造の細かな違いが価値に影響するブランドです。",
      items: [
        {
          index: "01",
          title: "フリルやレースを単なる装飾として見ない",
          text:
            "taoの装飾は、服の形や重さ、身体との距離を作る要素です。装飾密度と構造をあわせて確認します。",
        },
        {
          index: "02",
          title: "tricotとtaoを文脈ごとに見る",
          text:
            "同じ系譜の服でも、tricot時代とtaoでは評価軸や需要が異なります。ラインと年代を分けて判断します。",
        },
        {
          index: "03",
          title: "繊細な素材の状態を丁寧に見る",
          text:
            "レース、チュール、白系素材、刺繍、パッチワークは状態差が大きく出ます。次の着用者へ渡せる価値があるかを確認します。",
        },
      ],
    },
    flow: {
      label: "07 / BUYBACK FLOW",
      titleLines: [
        "LINE事前査定から、",
        "宅配買取へ。",
      ],
      items: [
        {
          step: "STEP 01",
          title: "LINEで事前査定",
          text:
            "写真と簡単な情報をお送りください。全体、ブランドタグ、品番タグ、素材表記、装飾部分、汚れやほつれが分かる写真があると確認しやすくなります。",
        },
        {
          step: "STEP 02",
          title: "宅配買取を申込",
          text:
            "本査定をご希望の場合は申込フォームへ。宅配キットの要否もここで選択できます。",
        },
        {
          step: "STEP 03",
          title: "無料で発送",
          text:
            "送料は無料。レースやフリルなど繊細な装飾がある場合は、なるべく形を崩さない梱包をおすすめします。",
        },
        {
          step: "STEP 04",
          title: "本査定・ご入金",
          text:
            "到着後に状態・真贋・市場性・アーカイブ価値を確認。金額にご納得いただければご指定口座へ入金します。",
        },
      ],
    },
    assurance: {
      label: "08 / SAFE BUYBACK",
      titleLines: [
        "相談しやすく、",
        "進めやすいtao COMME des GARÇONS買取。",
      ],
      intro:
        "LINEで相談してから進められるため、繊細な素材や過去シーズンのアイテムも、宅配買取へ進む前に確認できます。",
      items: [
        {
          title: "LINE事前査定あり",
          text:
            "まず相談してから進められるため、状態や市場性に不安がある品も事前に確認できます。",
        },
        {
          title: "送料は無料",
          text:
            "宅配買取の発送費用はEnclopediaが負担します。",
        },
        {
          title: "宅配キットは選択制",
          text:
            "必要な方には宅配キットをご用意。ご自身で梱包できる場合はそのまま発送可能です。",
        },
        {
          title: "キャンセル返送料は基本無料",
          text:
            "査定金額にご納得いただけない場合の返送も基本無料です。",
        },
      ],
      notice:
        "キャンセル返送料は基本無料です。ただし、お送りいただいた品がすべてコピー品、または真贋判定不可品のみだった場合は、返送料をご負担いただく場合があります。",
    },
    faq: {
      label: "09 / FAQ",
      titleLines: [
        "tao COMME des GARÇONS買取で、",
        "よくあるご質問。",
      ],
      items: [
        {
          question:
            "どのtao COMME des GARÇONSアイテムを買取していますか？",
          answer:
            "ワンピース、ブラウス、スカート、パンツ、ジャケット、コート、ニット、カットソー、小物まで幅広くご相談いただけます。",
        },
        {
          question:
            "tricot COMME des GARÇONSも査定できますか？",
          answer:
            "はい。tricot COMME des GARÇONSも査定対象です。年代、素材、柄、状態、現在需要を確認して個別に判断します。",
        },
        {
          question:
            "フリルやレースのある服は高く売れますか？",
          answer:
            "状態・デザイン・シーズン性・現在需要によって変動しますが、taoらしい素材表現や装飾性が強いアイテムは丁寧に評価します。",
        },
        {
          question:
            "白や淡色のアイテムに少し汚れがあります。査定できますか？",
          answer:
            "査定可能です。シミ、黄ばみ、ほつれ、レース破れなどの状態を確認したうえで、再流通価値を判断します。",
        },
        {
          question:
            "古いtaoやtricotのアーカイブも買取できますか？",
          answer:
            "はい。年代だけで一律には判断せず、希少性、素材表現、柄、現在の需要を踏まえて個別に査定します。",
        },
        {
          question:
            "tao COMME des GARÇONSの買取相場を先に知りたいです。",
          answer:
            "まずはLINEで写真をお送りください。ライン、品番、状態、サイズ、素材、現在需要を確認し、宅配買取へ進む前に大まかな方向性をご案内します。",
        },
      ],
    },
    finalCta: {
      titleLines: [
        "まずはLINEで、",
        "tao COMME des GARÇONSの価値をご相談ください。",
      ],
      body:
        "tao、tricot COMME des GARÇONS、フリル、レース、刺繍、ワンピース、アーカイブ作品まで確認。\nEnclopediaが、繊細な造形と再流通価値を見て宅配買取へ丁寧につなげます。",
      primaryLabel: "LINEで事前査定する",
      primaryHref: "https://line.me/R/ti/p/@240yzqkh",
      secondaryLabel: "宅配買取を申し込む",
      secondaryHref:
        "https://www.enclopedia-official.com/kaitori/takuhai",
    },
  },
];

function renderLines(lines) {
  return lines.map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
}

function Seo({ article, canonicalUrl }) {
  const { title, description, keyword } = article.seo;
  const ogImage = article.hero.image
    ? `${process.env.REACT_APP_IMAGE_URL || ""}${article.hero.image}`
    : `${process.env.PUBLIC_URL || ""}/favicon.ico`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          mainEntityOfPage: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: "Enclopedia",
          },
          about: [
            "ブランド買取",
            "宅配買取",
            article.listTitle,
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: typeof window !== "undefined" ? window.location.origin : "",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Brand Buyback",
              item: `${typeof window !== "undefined" ? window.location.origin : ""}/brand_buyback`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.listTitle,
              item: canonicalUrl,
            },
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.faq.items.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        })}
      </script>
    </Helmet>
  );
}

function IndexSeo() {
    const title =
    "ブランド買取｜アーカイブ価値まで見て査定するEnclopedia";

  const description =
    "Enclopediaのブランド買取ページ一覧。デザイナーズブランドのシーズン背景・コレクション性・希少性まで見て査定し、LINE事前査定から宅配買取までご案内します。";

  const keyword =
    "ブランド別買取,デザイナーズブランド 買取,アーカイブ買取,ブランド古着 買取,宅配買取,LINE査定,Enclopedia";

  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/brand_buyback`;

  useEffect(() => {
    headRewrite(title, description, keyword);
  }, [title, description, keyword]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keyword} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Enclopedia" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: {
            "@type": "WebSite",
            name: "Enclopedia",
            url: typeof window !== "undefined" ? window.location.origin : "",
          },
        })}
      </script>
    </Helmet>
  );
}

function BrandBuybackIndex({ classes }) {
  return (
    <div className={classes.root}>
      <IndexSeo />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.indexHero}>
            <div className={classes.indexIntro}>
              <div className={classes.labelRow}>
                <span>BRAND BUYBACK</span>
                <span className={classes.labelLine} />
              </div>
              <Typography variant="h1" component="h1" className={classes.heroTitle}>
                ブランドごとの価値を、
                <br />
                文脈まで見て査定する。
              </Typography>
              <Typography component="p" className={classes.heroBody}>
                Enclopediaでは、シーズン背景・コレクション性・希少性を重視したブランド別買取ページを用意しています。
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.articleGrid}>
              {brandBuybackArticles.map((article) => (
                <a
                  href={`/brand_buyback/${article.slug}`}
                  className={classes.articleCard}
                  key={article.slug}
                >
                  <div>
                    <div className={classes.articleCategory}>{article.category}</div>
                    <Typography component="h2" className={classes.articleTitle}>
                      {article.listTitle}
                    </Typography>
                    <Typography component="p" className={classes.articleCardBody}>
                      {article.listDescription}
                    </Typography>
                  </div>
                  <div className={classes.articleArrow}>ページを見る →</div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </Container>
    </div>
  );
}

function BrandBuybackNotFound({ classes }) {
  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.notFound}>
          <div>
            <Typography component="h1" className={classes.heading}>
              買取ページが見つかりませんでした。
            </Typography>
            <Button className={classes.primaryButton} component="a" href="/brand_buyback">
              ブランド別買取一覧へ戻る
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}

function BrandBuybackDetail({ classes, article }) {
  const canonicalUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${article.seo.canonicalPath}`;
  const { hero, thesis } = article;

  return (
    <div className={classes.root}>
      <Seo article={article} canonicalUrl={canonicalUrl} />
      <Container maxWidth="xl">
        <div className={classes.pageFrame}>
          <section className={classes.hero}>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={7}>
                <div className={classes.labelRow}>
                  <span>{hero.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography variant="h1" component="h1" className={classes.heroTitle}>
                  {renderLines(hero.titleLines)}
                </Typography>
                <Typography component="p" className={classes.heroBody}>
                  {hero.body}
                </Typography>
                <div className={classes.buttonRow}>
                  <Button className={classes.primaryButton} component="a" href={hero.primaryCtaHref}>
                    {hero.primaryCtaLabel}
                  </Button>
                  <Button className={classes.secondaryButton} component="a" href={hero.secondaryCtaHref}>
                    {hero.secondaryCtaLabel}
                  </Button>
                </div>
                <div className={classes.keywordLine}>
                  {hero.keywords.map((keyword, index) => (
                    <React.Fragment key={keyword}>
                      <span>{keyword}</span>
                      {index < hero.keywords.length - 1 && <span>/</span>}
                    </React.Fragment>
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} md={5}>
                <div className={classes.heroVisual}>
                  <div className={classes.heroCircle} />
                  <div className={classes.heroSquare} />
                  <div className={classes.heroNumber}>{hero.number}</div>
                  <div className={classes.heroMock}>
                    <img
                      src={`${process.env.REACT_APP_IMAGE_URL || ""}${hero.image}`}
                      alt={hero.imageAlt}
                      className={classes.heroMockImage}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div className={classes.labelRow}>
                  <span>{article.intro.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.intro.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.intro.body}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className={classes.thesisCard}>
                  <div>
                    <div className={classes.thesisLabel}>KEY THESIS</div>
                    <Typography component="p" className={classes.thesisText}>
                      {thesis.text}
                    </Typography>
                  </div>
                  <div className={classes.keywordLine}>
                    {thesis.keywords.map((keyword, index) => (
                      <React.Fragment key={keyword}>
                        <span>{keyword}</span>
                        {index < thesis.keywords.length - 1 && <span>/</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.valuation.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.valuation.titleLines)}
            </Typography>
            <div className={classes.valuationGrid}>
              {article.valuation.items.map((item) => (
                <div className={classes.valuationCard} key={item.number}>
                  <div>
                    <div className={classes.cardNumber}>{item.number}</div>
                    <div className={classes.cardTitle}>{item.title}</div>
                    <div className={classes.smallRule} />
                    <Typography component="p" className={classes.body}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.targetLines.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.targetLines.titleLines)}
            </Typography>
            <div className={classes.brandGrid}>
              {article.targetLines.items.map((item) => (
                <div className={classes.brandItem} key={item.title}>
                  <div className={classes.brandTag}>{item.tag}</div>
                  <Typography component="h3" className={classes.brandTitle}>
                    {item.title}
                  </Typography>
                  <Typography component="p" className={classes.noteBody}>
                    {item.text}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.highValueItems.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.highValueItems.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.highValueItems.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {article.highValueItems.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.quoteBlock}>
              <div className={classes.quoteMark}>“</div>
              <Typography component="p" className={classes.quoteText}>
                {renderLines(article.quote.textLines)}
              </Typography>
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.assessmentDifference.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.assessmentDifference.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.assessmentDifference.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.listBlock}>
                  {article.assessmentDifference.items.map((item) => (
                    <div className={classes.listItem} key={item.index}>
                      <div className={classes.listIndex}>{item.index}</div>
                      <div className={classes.listTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section}>
            <div className={classes.labelRow}>
              <span>{article.flow.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.flow.titleLines)}
            </Typography>
            <div className={classes.flowGrid}>
              {article.flow.items.map((item) => (
                <div className={classes.flowCard} key={item.step}>
                  <div>
                    <div className={classes.flowStep}>{item.step}</div>
                    <Typography component="h3" className={classes.flowTitle}>
                      {item.title}
                    </Typography>
                    <Typography component="p" className={classes.noteBody}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={classes.section}>
            <Grid container spacing={6}>
              <Grid item xs={12} md={5}>
                <div className={classes.labelRow}>
                  <span>{article.assurance.label}</span>
                  <span className={classes.labelLine} />
                </div>
                <Typography component="h2" className={classes.heading}>
                  {renderLines(article.assurance.titleLines)}
                </Typography>
                <Typography component="p" className={classes.body}>
                  {article.assurance.intro}
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.assuranceGrid}>
                  {article.assurance.items.map((item) => (
                    <div className={classes.assuranceItem} key={item.title}>
                      <div className={classes.assuranceTitle}>{item.title}</div>
                      <Typography component="p" className={classes.noteBody}>
                        {item.text}
                      </Typography>
                    </div>
                  ))}
                </div>
                <div className={classes.noteBox}>
                  <Typography component="p" className={classes.noticeBody}>
                    {article.assurance.notice}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </section>

          <section className={classes.section} id="faq">
            <div className={classes.labelRow}>
              <span>{article.faq.label}</span>
              <span className={classes.labelLine} />
            </div>
            <Typography component="h2" className={classes.heading}>
              {renderLines(article.faq.titleLines)}
            </Typography>
            <div className={classes.faqGrid}>
              {article.faq.items.map((faq) => (
                <div className={classes.faqItem} key={faq.question}>
                  <div className={classes.faqQuestion}>{faq.question}</div>
                  <Typography component="p" className={classes.noteBody}>
                    {faq.answer}
                  </Typography>
                </div>
              ))}
            </div>
          </section>

          <div className={classes.finalCta}>
            <div className={classes.stripe} />
            <Typography component="h2" className={classes.finalTitle}>
              {renderLines(article.finalCta.titleLines)}
            </Typography>
            <Typography component="p" className={classes.heroBody} style={{ margin: "0 auto 32px" }}>
              {article.finalCta.body}
            </Typography>
            <div className={classes.finalButtons}>
              <Button className={classes.primaryButton} component="a" href={article.finalCta.primaryHref}>
                {article.finalCta.primaryLabel}
              </Button>
              <Button className={classes.secondaryButton} component="a" href={article.finalCta.secondaryHref}>
                {article.finalCta.secondaryLabel}
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function BrandBuybackScreen() {
  const classes = useStyles();
  const { slug } = useParams();

  if (!slug) {
    return <BrandBuybackIndex classes={classes} />;
  }

  const article = brandBuybackArticles.find((item) => item.slug === slug);

  if (!article) {
    return <BrandBuybackNotFound classes={classes} />;
  }

  return <BrandBuybackDetail classes={classes} article={article} />;
}
